import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { Card, CardHeader, CardContent, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import WatchlistViewTabs from '../components/WatchlistViewTabs';
import ProfileMenu from '../components/ProfileMenu';
import PollSpreadsheet from './PollSpreadsheet';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  RefreshCw,
  Plus,
  Trash2,
  ExternalLink,
  Search,
  Filter,
  Download,
  FileText,
  X,
  Folder as FolderIcon,
  ChevronLeft,
  CheckCircle,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

// Development-only logging - no-op in production
const isDev = process.env.NODE_ENV === 'development';
const devLog = isDev ? console.log.bind(console) : () => {};

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Column definitions with sort keys - mobile property indicates if shown on mobile
// Note: Sector column removed since watchlists are already organized by sector
const COLUMNS = [
  { key: 'ticker', label: 'Ticker', sortable: true, width: 'w-20', mobile: true },
  { key: 'market_cap', label: 'MCap', sortable: true, width: 'w-24', align: 'right', mobile: false },
  { key: 'enterprise_value', label: 'EV', sortable: true, width: 'w-24', align: 'right', mobile: false },
  { key: 'forward_pe', label: 'P/E', sortable: true, width: 'w-20', align: 'right', mobile: false },
  { key: 'ev_to_sales', label: 'EV/S', sortable: true, width: 'w-20', align: 'right', mobile: false },
];

// Format large numbers - no decimals for currency, decimals for ratios
const formatNumber = (value, type = 'number') => {
  if (value === null || value === undefined || value === 'N/A' || value === 'Loading...') return '-';
  
  if (type === 'currency') {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    // Handle negative numbers by using absolute value for formatting, then add sign
    const absNum = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    if (absNum >= 1e12) return `${sign}$${(absNum / 1e12).toFixed(1)}T`;
    if (absNum >= 1e9) return `${sign}$${(absNum / 1e9).toFixed(1)}B`;
    if (absNum >= 1e6) return `${sign}$${(absNum / 1e6).toFixed(1)}M`;
    return `${sign}$${Math.round(absNum).toLocaleString()}`;
  }
  
  if (type === 'percent') {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `${num.toFixed(2)}%`;
  }
  
  if (type === 'ratio') {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return num.toFixed(2);
  }
  
  return value;
};

// Sort comparison function
const compareValues = (a, b, key, direction) => {
  let valA = a[key];
  let valB = b[key];
  
  // Handle null/undefined
  if (valA == null || valA === '' || valA === 'N/A' || valA === 'Pro plan required') valA = null;
  if (valB == null || valB === '' || valB === 'N/A' || valB === 'Pro plan required') valB = null;
  
  // Push nulls to the end regardless of sort direction
  if (valA === null && valB === null) return 0;
  if (valA === null) return 1;
  if (valB === null) return -1;
  
  // Parse values with magnitude suffixes (T, B, M, K)
  const parseWithMagnitude = (val) => {
    if (typeof val === 'number') return val;
    const str = String(val).replace(/[$,%]/g, '').trim();
    const match = str.match(/^(-?\d+\.?\d*)\s*([TBMKtbmk])?$/);
    if (match) {
      let num = parseFloat(match[1]);
      const suffix = (match[2] || '').toUpperCase();
      if (suffix === 'T') num *= 1e12;
      else if (suffix === 'B') num *= 1e9;
      else if (suffix === 'M') num *= 1e6;
      else if (suffix === 'K') num *= 1e3;
      return num;
    }
    return parseFloat(str);
  };
  
  const numA = parseWithMagnitude(valA);
  const numB = parseWithMagnitude(valB);
  
  if (!isNaN(numA) && !isNaN(numB)) {
    return direction === 'asc' ? numA - numB : numB - numA;
  }
  
  // String comparison
  const strA = String(valA).toLowerCase();
  const strB = String(valB).toLowerCase();
  
  if (direction === 'asc') {
    return strA.localeCompare(strB);
  }
  return strB.localeCompare(strA);
};

// Helper function to calculate days old
const calculateDaysOld = (dateStr) => {
  if (!dateStr) return 999;
  const date = new Date(dateStr);
  const now = new Date();
  return Math.floor((now - date) / (1000 * 60 * 60 * 24));
};

// Helper function to get age category
const getAgeCategory = (dateStr) => {
  const daysOld = calculateDaysOld(dateStr);
  if (daysOld <= 3) return 'Fresh';
  if (daysOld <= 14) return 'Warm';
  return 'Stale';
};

// Helper function to format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric'
  });
};

// X-Compliant Reply Card Component - per X/Twitter Display Requirements
// All elements must link to appropriate destinations: avatar/name/username to profile, timestamp to post
const XCompliantReplyCard = ({ 
  reply, 
  replyIdx, 
  status, 
  isDeleted, 
  displayContent, 
  isLong, 
  isContentExpanded, 
  contentKey, 
  setExpandedContent, 
  isDarkMode, 
  renderTextWithLinks 
}) => {
  const replyXProfileUrl = reply.author_username ? `https://x.com/${reply.author_username.replace('@', '')}` : null;
  const replySourceUrl = reply.url;
  
  return (
    <div className={`${isDeleted ? 'opacity-50' : ''}`}>
      {/* Clickable white space area linking to permalink */}
      <a 
        href={replySourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="block cursor-pointer"
      >
        <div className="flex items-start gap-3">
          {/* Profile Image - MUST link to X profile */}
          {(reply.author_profile_image || reply.profile_image_url || reply.profileImageUrl) ? (
            <span 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(replyXProfileUrl, '_blank');
              }}
              className="flex-shrink-0"
            >
              <img 
                src={reply.author_profile_image || reply.profile_image_url || reply.profileImageUrl} 
                alt={reply.author_name || reply.author_username}
                className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
              />
            </span>
          ) : (
            <span 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(replyXProfileUrl, '_blank');
              }}
              className="flex-shrink-0"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ${isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}>
                <svg className={`w-6 h-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.7 0 4.875-2.175 4.875-4.875S14.7 2.25 12 2.25 7.125 4.425 7.125 7.125 9.3 12 12 12zm0 2.25c-3.262 0-9.75 1.637-9.75 4.875V21h19.5v-1.875c0-3.238-6.488-4.875-9.75-4.875z"/>
                </svg>
              </div>
            </span>
          )}
          
          <div className="flex-1 min-w-0">
            {/* Name and handle stacked vertically with X Logo */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {/* Author Name - MUST link to X profile */}
                {reply.author_name && (
                  <span 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(replyXProfileUrl, '_blank');
                    }}
                    className={`font-bold block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                  >
                    {reply.author_name}
                  </span>
                )}
                {/* @username below name - MUST link to X profile */}
                <div className="flex items-center gap-1">
                  <span 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(replyXProfileUrl, '_blank');
                    }}
                    className={`block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                  >
                    @{reply.author_username}
                  </span>
                  {status?.status === 'checking' && <Loader2 className={`w-3 h-3 animate-spin ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />}
                  {status?.status === 'deleted' && <AlertTriangle className="w-3 h-3 text-amber-500" />}
                </div>
              </div>
              
              {/* X Logo - upper right per X Display Requirements */}
              <svg className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
              </svg>
            </div>
          </div>
        </div>
      </a>
      
      {/* Timestamp + View on X - below author header */}
      <div className="flex items-center gap-2 mt-1 mb-2" style={{marginLeft: '52px'}}>
        {reply.created_at && (
          <a
            href={replySourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`text-xs hover:underline ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
          >
            {new Date(reply.created_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </a>
        )}
        {/* View on X next to timestamp */}
        {replySourceUrl && !isDeleted && (
          <>
            <span className={`${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}>·</span>
            <a
              href={replySourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-blue-500 hover:underline"
            >
              View on X
            </a>
          </>
        )}
      </div>
      
      {/* Tweet Content */}
      {isDeleted ? (
        <p className={`text-xs italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} style={{marginLeft: '52px'}}>Post removed from X</p>
      ) : (
        <div className={`text-sm leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`} style={{marginLeft: '52px'}}>
          {renderTextWithLinks(displayContent, isDarkMode)}{isLong && !isContentExpanded && '...'}
          {isLong && !isContentExpanded && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setExpandedContent(prev => ({ ...prev, [contentKey]: true }));
              }}
              className={`ml-1 text-xs font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
            >
              more...
            </button>
          )}
        </div>
      )}
      
      {/* Engagement Metrics */}
      {(reply.metrics || reply.public_metrics) && (
        <div className={`flex items-center gap-4 mt-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`} style={{marginLeft: '52px'}}>
          {((reply.metrics || reply.public_metrics)?.reply_count > 0) && (
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
              {((reply.metrics || reply.public_metrics)?.reply_count || 0).toLocaleString()}
            </span>
          )}
          {((reply.metrics || reply.public_metrics)?.retweet_count > 0) && (
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
              </svg>
              {((reply.metrics || reply.public_metrics)?.retweet_count || 0).toLocaleString()}
            </span>
          )}
          {((reply.metrics || reply.public_metrics)?.like_count > 0) && (
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
              </svg>
              {((reply.metrics || reply.public_metrics)?.like_count || 0).toLocaleString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

function WatchlistSpreadsheet({ onTickerClick, isDarkMode = false, setIsDarkMode, onOriginal, onDocuments, onSchedule, onShare, onDelete, onDeleteSharedItem, onConfirmAction, initialWatchlistId = null, onSearch, showNotification, sharedWatchlists = [], watchlistView = 'my', setWatchlistView, onLogout, onNavigateToAccount, onNavigateToSchedules, ideasLists = [], polls = [], sharedPolls = [], onWatchlist, onPollUpdated, onCreatePoll, onDeletePoll, isWorkspaceMode = false }) {
  // State for viewing a poll within Collections
  const [selectedCollectionPoll, setSelectedCollectionPoll] = useState(null);
  // State for the "New" dropdown menu
  const [showNewDropdown, setShowNewDropdown] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);
  const [tickerData, setTickerData] = useState([]);
  const [allTickersData, setAllTickersData] = useState(() => {
    // Clear localStorage cache to force fresh fetch
    // TODO: Remove this after initial deployment
    try {
      localStorage.removeItem('watchlistTickerCache');
      localStorage.removeItem('watchlistTickerCacheTimestamp');
      console.log('[WatchlistSpreadsheet] Cleared stale ticker cache');
    } catch (e) {
      console.warn('[WatchlistSpreadsheet] Failed to clear cache:', e);
    }
    return [];
  }); // All tickers from all watchlists
  const [loading, setLoading] = useState(false); // Start false to avoid blink on tab switch
  const [initialLoad, setInitialLoad] = useState(true); // Track if this is first load
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'ticker', direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  const [newTicker, setNewTicker] = useState('');
  const [addingTicker, setAddingTicker] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateWatchlist, setShowCreateWatchlist] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [creatingWatchlist, setCreatingWatchlist] = useState(false);
  const [rateLimit, setRateLimit] = useState(null);
  const [warnings, setWarnings] = useState(null);
  
  // Mobile expanded row state
  const [expandedRowTicker, setExpandedRowTicker] = useState(null);
  
  // New watchlist creation with ticker search
  const [tickerSearchQuery, setTickerSearchQuery] = useState('');
  const [tickerSearchResults, setTickerSearchResults] = useState([]);
  const [isSearchingTickers, setIsSearchingTickers] = useState(false);
  const [selectedTickers, setSelectedTickers] = useState([]);
  
  // Add ticker to existing watchlist
  const [showAddToWatchlist, setShowAddToWatchlist] = useState(false);
  const [addingToWatchlist, setAddingToWatchlist] = useState(false);
  
  // Watchlist dropdown state
  const [showWatchlistDropdown, setShowWatchlistDropdown] = useState(false);
  
  // Filter by watchlist type
  const [watchlistTypeFilter, setWatchlistTypeFilter] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  
  // View All state - accordion view showing all watchlists - default to true
  const [viewAllMode, setViewAllMode] = useState(true);
  const [expandedWatchlistIds, setExpandedWatchlistIds] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set()); // Track which categories are expanded (all collapsed by default)
  const [expandedWatchlists, setExpandedWatchlists] = useState({}); // Track which individual watchlists are expanded
  
  // Reply validation state for Requests/Polls
  const [validationStatus, setValidationStatus] = useState({});
  
  // Content expansion state - track which posts are fully expanded
  const [expandedContent, setExpandedContent] = useState({});
  
  // Curation original post expansion state
  const [expandedCurationContent, setExpandedCurationContent] = useState({});
  
  // Curation view mode state: 'summary' shows preview, 'full' shows original post
  const [curationViewMode, setCurationViewMode] = useState({});
  // Request view mode state: 'summary' shows preview, 'full' shows original post
  const [requestViewMode, setRequestViewMode] = useState({});
  
  // Helper to decode HTML entities
  const decodeHtmlEntities = (text) => {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };
  
  // Helper function to render text with clickable URLs
  const renderTextWithLinks = (text, isDark = false) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s<]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(/^https?:\/\//)) {
        // Clean trailing punctuation
        let cleanUrl = part.replace(/[.,;:!?)\]]+$/, '');
        const punctuation = part.substring(cleanUrl.length);
        return (
          <span key={index}>
            <a
              href={cleanUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={`underline ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
            >
              {cleanUrl}
            </a>
            {punctuation}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };
  
  // Validate a reply to check if it still exists on X (for compliance)
  const validateReply = useCallback(async (reply) => {
    if (!reply?.id || !reply?.url) return;
    
    const replyId = reply.id;
    
    // Skip if already validated recently
    if (validationStatus[replyId]?.status === 'valid' || validationStatus[replyId]?.status === 'deleted') {
      return;
    }
    
    setValidationStatus(prev => ({ ...prev, [replyId]: { status: 'checking', cached: false } }));
    
    try {
      // Extract tweet ID from URL
      const tweetIdMatch = reply.url.match(/status\/(\d+)/);
      const tweetId = tweetIdMatch ? tweetIdMatch[1] : reply.id;
      
      const response = await fetch(`${API_BASE_URL}/api/tweets/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tweet_id: tweetId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setValidationStatus(prev => ({
          ...prev,
          [replyId]: {
            status: data.is_deleted ? 'deleted' : 'valid',
            cached: data.from_cache || false
          }
        }));
      } else {
        // API error - assume valid to not block user
        setValidationStatus(prev => ({ ...prev, [replyId]: { status: 'valid', cached: false } }));
      }
    } catch (error) {
      console.error('Error validating reply:', error);
      setValidationStatus(prev => ({ ...prev, [replyId]: { status: 'valid', cached: false } }));
    }
  }, [validationStatus]);
  
  // Toggle category expansion
  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };
  
  // Toggle watchlist expansion AND fetch metrics when expanding
  const toggleWatchlistExpansion = (watchlistId, watchlist) => {
    const isCurrentlyExpanded = expandedWatchlists[watchlistId];
    
    // Update expansion state
    setExpandedWatchlists(prev => ({ ...prev, [watchlistId]: !isCurrentlyExpanded }));
    
    // If expanding (not collapsing), fetch metrics for this watchlist's tickers
    if (!isCurrentlyExpanded && watchlist) {
      const categorizedTickers = watchlist?.source_info?.categorized_tickers || [];
      const allCategoryTickers = categorizedTickers.flatMap(cat => cat.tickers || []);
      const allTickers = [...new Set([
        ...(watchlist.tickers || []),
        ...allCategoryTickers
      ])];
      
      if (allTickers.length > 0) {
        console.log('[WatchlistSpreadsheet] Expanding watchlist, fetching metrics for', allTickers.length, 'tickers:', allTickers.slice(0, 5));
        // Fetch and merge into allTickersData
        fetchTickerMetrics(allTickers, true);
      } else {
        console.log('[WatchlistSpreadsheet] No tickers to fetch for watchlist:', watchlistId);
      }
    }
  };
  
  // Handle ticker click - use prop callback if provided, otherwise navigate
  const handleTickerClick = (ticker) => {
    if (onTickerClick) {
      onTickerClick(ticker);
    } else {
      // Fallback for standalone page usage
      navigate(`/?tab=watchlists&open_ticker=${ticker}`);
    }
  };

  // Handle initialWatchlistId changes - but only if not in viewAllMode
  useEffect(() => {
    if (initialWatchlistId && watchlists.length > 0 && !viewAllMode) {
      const targetWatchlist = watchlists.find(wl => wl.id === initialWatchlistId);
      if (targetWatchlist && targetWatchlist.id !== selectedWatchlist?.id) {
        setSelectedWatchlist(targetWatchlist);
      }
    }
  }, [initialWatchlistId, watchlists, selectedWatchlist?.id, viewAllMode]);

  // Fetch watchlists
  const fetchWatchlists = useCallback(async () => {
    if (!user?.id) return;
    
    // Only show loading on initial load when we have no data
    if (initialLoad && watchlists.length === 0) {
      setLoading(true);
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/watchlists/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setWatchlists(data.watchlists || []);
        
        // Only auto-select if initialWatchlistId is provided (e.g., from global search) AND not in viewAllMode
        if (initialWatchlistId && !selectedWatchlist && !viewAllMode && data.watchlists?.length > 0) {
          const targetWatchlist = data.watchlists.find(wl => wl.id === initialWatchlistId);
          if (targetWatchlist) {
            setSelectedWatchlist(targetWatchlist);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching watchlists:', err);
      setError('Failed to load watchlists');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [user?.id, selectedWatchlist, initialWatchlistId, initialLoad, watchlists.length]);

  // Fetch ticker metrics for a watchlist
  const fetchTickerMetrics = useCallback(async (tickers, isAllTickers = false) => {
    if (!tickers || tickers.length === 0) {
      if (isAllTickers) {
        setAllTickersData([]);
      } else {
        setTickerData([]);
      }
      return;
    }
    
    // For allTickers ONLY (not regular tickerData), check if we already have cached data from today
    // Skip cache check for regular tickerData to ensure metrics always load on watchlist selection
    if (isAllTickers && allTickersData.length > 0) {
      // Check if the cached tickers cover what we need WITH VALID DATA
      // A ticker is only considered "cached" if it has at least one metric value
      const validCachedTickers = new Set(
        allTickersData
          .filter(t => t.ticker && (t.market_cap || t.enterprise_value || t.forward_pe))
          .map(t => t.ticker.toUpperCase())
      );
      const missingTickers = tickers.filter(t => !validCachedTickers.has(t.toUpperCase()));
      
      if (missingTickers.length === 0) {
        console.log('[WatchlistSpreadsheet] All tickers already cached with valid data');
        return;
      }
      
      console.log(`[WatchlistSpreadsheet] ${missingTickers.length} tickers missing or without valid data, fetching...`);
      // Only fetch missing tickers
      tickers = missingTickers;
    }
    
    console.log('[WatchlistSpreadsheet] fetchTickerMetrics:', {
      isAllTickers,
      tickerCount: tickers.length,
      tickers: tickers.slice(0, 5)
    });
    
    if (!isAllTickers) {
      setLoadingMetrics(true);
    }
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/watchlist-spreadsheet/metrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers, user_id: user?.id })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[WatchlistSpreadsheet] API response:', {
          isAllTickers,
          returnedCount: data.tickers?.length,
          sampleData: data.tickers?.slice(0, 2).map(t => ({ ticker: t.ticker, market_cap: t.market_cap }))
        });
        if (isAllTickers) {
          // Merge with existing cached data
          const newData = data.tickers || [];
          setAllTickersData(prev => {
            const existingMap = new Map(prev.map(t => [t.ticker, t]));
            newData.forEach(t => existingMap.set(t.ticker, t));
            const merged = Array.from(existingMap.values());
            
            // Save to localStorage
            try {
              localStorage.setItem('watchlistTickerCache', JSON.stringify(merged));
              localStorage.setItem('watchlistTickerCacheTimestamp', new Date().toISOString());
              console.log(`[WatchlistSpreadsheet] Saved ${merged.length} tickers to localStorage cache`);
            } catch (e) {
              console.warn('[WatchlistSpreadsheet] Failed to save cache to localStorage:', e);
            }
            
            return merged;
          });
        } else {
          setTickerData(data.tickers || []);
        }
        setRateLimit(data.rate_limit || null);
        setWarnings(data.warnings || null);
      } else {
        const errorText = await response.text();
        console.error('[WatchlistSpreadsheet] API error:', response.status, errorText);
        setError(`Failed to load ticker metrics (${response.status})`);
      }
    } catch (err) {
      console.error('[WatchlistSpreadsheet] Error fetching ticker metrics:', err);
      setError(`Failed to load ticker metrics: ${err.message}`);
    } finally {
      if (!isAllTickers) {
        setLoadingMetrics(false);
      }
    }
  }, [user?.id]);

  // Fetch all tickers from all watchlists (including categorized tickers)
  // REMOVED: Don't fetch ALL tickers on page load
  // Instead, use cached data from localStorage or fetch on-demand when user clicks a watchlist
  useEffect(() => {
    if (watchlists.length > 0 && allTickersData.length === 0) {
      // Only log, don't fetch - data will be fetched when user selects a watchlist
      // or if they need the "All Tickers" view
      const allTickers = [...new Set(watchlists.flatMap(wl => {
        const regularTickers = wl.tickers || [];
        const categorizedTickers = (wl.source_info?.categorized_tickers || []).flatMap(cat => cat.tickers || []);
        return [...regularTickers, ...categorizedTickers];
      }))];
      
      console.log('[WatchlistSpreadsheet] Watchlists loaded:', {
        watchlistCount: watchlists.length,
        totalTickers: allTickers.length,
        cachedTickers: allTickersData.length,
        note: 'NOT fetching on page load - will fetch on-demand'
      });
    }
  }, [watchlists, allTickersData.length]);

  // Search for tickers in user's database
  const searchUserTickers = async (query) => {
    if (!query.trim() || !user?.id) {
      setTickerSearchResults([]);
      return;
    }
    
    setIsSearchingTickers(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/search-ideas/${user.id}?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        const ideas = data.ideas || [];
        const queryLower = query.toLowerCase().replace('$', '');
        
        // Show all posts (not deduped) - user picks which post, but we add the ticker
        const results = ideas.map(idea => {
          const tickers = idea.tickers || [];
          // Find ticker that matches the search query
          const matchedTicker = tickers.find(t => {
            const tickerStr = typeof t === 'string' ? t : t?.ticker || '';
            return tickerStr.toLowerCase().includes(queryLower);
          });
          const displayTicker = matchedTicker 
            ? (typeof matchedTicker === 'string' ? matchedTicker : matchedTicker?.ticker)
            : (tickers[0]?.ticker || tickers[0] || '');
          
          return {
            ticker: displayTicker,
            company_name: idea.title || idea.one_liner_thesis || idea.company_name || displayTicker,
            author: idea.author_handle || idea.author_name || idea.tweet_author || idea.from_name,
            source: idea.source_category || (idea.is_ticker_list ? 'ticker_list' : 'idea'),
            idea_id: idea.id,
            is_ticker_list: idea.is_ticker_list
          };
        }).filter(r => r.ticker);
        
        setTickerSearchResults(results);
      }
    } catch (err) {
      console.error('Error searching tickers:', err);
    } finally {
      setIsSearchingTickers(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (tickerSearchQuery) {
        searchUserTickers(tickerSearchQuery);
      } else {
        setTickerSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [tickerSearchQuery, user?.id]);

  // Add ticker to selected list
  const addTickerToSelection = (tickerInfo) => {
    if (!selectedTickers.find(t => t.ticker === tickerInfo.ticker)) {
      setSelectedTickers(prev => [...prev, tickerInfo]);
    }
    // Don't clear search - let user select multiple from results
  };

  // Remove ticker from selected list
  const removeTickerFromSelection = (ticker) => {
    setSelectedTickers(prev => prev.filter(t => t.ticker !== ticker));
  };

  // Create a new watchlist with selected tickers
  const handleCreateWatchlist = async () => {
    if (!newWatchlistName.trim() || !user?.id) return;
    
    setCreatingWatchlist(true);
    try {
      const tickersToAdd = selectedTickers.map(t => t.ticker);
      
      // Create as a custom list via watchlists API
      const response = await fetch(`${API_BASE_URL}/api/watchlists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newWatchlistName.trim(),
          description: '',
          user_id: user.id,
          tickers: tickersToAdd,
          list_type: 'custom_list'
        })
      });
      
      if (response.ok) {
        setNewWatchlistName('');
        setSelectedTickers([]);
        setTickerSearchQuery('');
        setShowCreateWatchlist(false);
        
        // Refresh watchlists to show the new custom list
        await fetchWatchlists();
        
        if (showNotification) {
          showNotification(`Custom list "${newWatchlistName.trim()}" created!`, 'success');
        }
      }
    } catch (err) {
      console.error('Error creating custom list:', err);
      setError('Failed to create custom list');
    } finally {
      setCreatingWatchlist(false);
    }
  };

  // Add tickers to existing watchlist
  const handleAddToWatchlist = async () => {
    if (!selectedWatchlist || selectedTickers.length === 0 || !user?.id) {
      console.log('Missing requirements:', { selectedWatchlist: !!selectedWatchlist, tickersCount: selectedTickers.length, userId: user?.id });
      return;
    }
    
    setAddingToWatchlist(true);
    try {
      const newTickers = selectedTickers.map(t => t.ticker);
      
      // Check if this is an Ideas List (has categorized_tickers)
      const isIdeasList = selectedWatchlist?.source_info?.categorized_tickers?.length > 0;
      
      let combinedTickers;
      if (isIdeasList) {
        // For Ideas Lists, just send the new tickers - backend will handle adding to Uncategorized category
        combinedTickers = newTickers;
      } else {
        // For regular watchlists, combine with existing tickers
        const currentTickers = selectedWatchlist.tickers || [];
        combinedTickers = [...new Set([...currentTickers, ...newTickers])];
      }
      
      const response = await fetch(`${API_BASE_URL}/api/watchlists/${selectedWatchlist.id}/tickers`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tickers: combinedTickers,
          user_id: user.id
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setSelectedTickers([]);
        setTickerSearchQuery('');
        setShowAddToWatchlist(false);
        await fetchWatchlists();
        
        // Refresh metrics for the updated tickers
        const tickersToRefresh = result.tickers || combinedTickers;
        if (tickersToRefresh.length > 0) {
          fetchTickerMetrics(tickersToRefresh, false);
        }
        
        // Log success with details
        if (result.is_ideas_list) {
          console.log(`Added tickers to Ideas List: ${result.message}`);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        setError(errorData.detail || 'Failed to add tickers to watchlist');
      }
    } catch (err) {
      console.error('Error adding to watchlist:', err);
      setError('Failed to add tickers to watchlist');
    } finally {
      setAddingToWatchlist(false);
    }
  };

  // Load watchlists on mount
  useEffect(() => {
    fetchWatchlists();
  }, [fetchWatchlists]);

  // Listen for import complete event to refresh watchlists
  useEffect(() => {
    const handleImportComplete = (event) => {
      console.log('[WatchlistSpreadsheet] Import complete event received, refreshing watchlists...', event.detail);
      fetchWatchlists();
    };
    
    window.addEventListener('importComplete', handleImportComplete);
    return () => window.removeEventListener('importComplete', handleImportComplete);
  }, [fetchWatchlists]);

  // Load metrics when watchlist changes
  useEffect(() => {
    const loadMetrics = async () => {
      console.log('[WatchlistSpreadsheet] useEffect triggered - selectedWatchlist:', selectedWatchlist?.id, 'name:', selectedWatchlist?.name);
      if (selectedWatchlist) {
        // For categorized watchlists (Ideas Lists), extract all tickers from categories
        const categorizedTickers = selectedWatchlist?.source_info?.categorized_tickers || [];
        const allCategoryTickers = categorizedTickers.flatMap(cat => cat.tickers || []);
        
        console.log('[WatchlistSpreadsheet] Extracting tickers - regular:', selectedWatchlist.tickers?.length, 'categorized:', allCategoryTickers.length);
        
        // Combine regular tickers and categorized tickers
        const allTickers = [...new Set([
          ...(selectedWatchlist.tickers || []),
          ...allCategoryTickers
        ])];
        
        console.log('[WatchlistSpreadsheet] Combined unique tickers:', allTickers.length, allTickers.slice(0, 5));
        
        if (allTickers.length > 0) {
          console.log('[WatchlistSpreadsheet] Watchlist changed, fetching metrics for', allTickers.length, 'tickers');
          await fetchTickerMetrics(allTickers);
        } else {
          console.log('[WatchlistSpreadsheet] No tickers to fetch, setting empty data');
          setTickerData([]);
        }
      } else {
        console.log('[WatchlistSpreadsheet] No selectedWatchlist, clearing data');
        setTickerData([]);
      }
    };
    
    loadMetrics();
  }, [selectedWatchlist]); // Depend on entire selectedWatchlist object

  // Close watchlist dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showWatchlistDropdown && !event.target.closest('.watchlist-dropdown-container')) {
        setShowWatchlistDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWatchlistDropdown]);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sorted and filtered data - search across ALL tickers when searching
  const displayData = useMemo(() => {
    // Use allTickersData when searching, otherwise use current watchlist's tickerData
    let data = filterText ? [...allTickersData] : [...tickerData];
    
    // Debug logging
    console.log('[WatchlistSpreadsheet] displayData computation:', {
      filterText: filterText,
      tickerDataCount: tickerData.length,
      allTickersDataCount: allTickersData.length,
      usingData: filterText ? 'allTickersData' : 'tickerData',
      resultCount: data.length,
      sampleTickers: data.slice(0, 3).map(d => ({ ticker: d.ticker, market_cap: d.market_cap, gross_margin: d.gross_margin }))
    });
    
    // Filter/Search
    if (filterText) {
      const filter = filterText.toLowerCase();
      data = data.filter(row => 
        Object.values(row).some(val => 
          String(val).toLowerCase().includes(filter)
        )
      );
    }
    
    // Sort
    if (sortConfig.key) {
      data.sort((a, b) => compareValues(a, b, sortConfig.key, sortConfig.direction));
    }
    
    return data;
  }, [tickerData, allTickersData, sortConfig, filterText]);

  // Add ticker to watchlist
  const handleAddTicker = async () => {
    if (!newTicker.trim() || !selectedWatchlist) return;
    
    setAddingTicker(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/watchlists/${selectedWatchlist.id}/tickers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker: newTicker.toUpperCase().trim() })
      });
      
      if (response.ok) {
        setNewTicker('');
        // Refresh watchlist
        await fetchWatchlists();
        // Refresh metrics
        const updatedTickers = [...(selectedWatchlist.tickers || []), newTicker.toUpperCase().trim()];
        await fetchTickerMetrics(updatedTickers);
      }
    } catch (err) {
      console.error('Error adding ticker:', err);
    } finally {
      setAddingTicker(false);
    }
  };

  // Remove ticker from watchlist
  const handleRemoveTicker = (ticker) => {
    if (!selectedWatchlist) return;
    
    // Call onDelete callback for tracking if provided
    if (onDelete) {
      onDelete({ ticker });
    }
    
    const doRemove = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/watchlists/${selectedWatchlist.id}/tickers/${ticker}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          // Update local state
          setTickerData(prev => prev.filter(t => t.ticker !== ticker));
          await fetchWatchlists();
          if (showNotification) {
            showNotification(`Removed $${ticker} from ${selectedWatchlist.name}`, 'success');
          }
        } else {
          if (showNotification) {
            showNotification(`Failed to remove $${ticker}`, 'error');
          }
        }
      } catch (err) {
        console.error('Error removing ticker:', err);
        if (showNotification) {
          showNotification(`Error removing $${ticker}`, 'error');
        }
      }
    };

    // Use custom confirm modal if available
    if (onConfirmAction) {
      onConfirmAction({
        title: 'Remove Ticker',
        message: `Are you sure you want to remove $${ticker} from "${selectedWatchlist.name}"?`,
        confirmText: 'Remove',
        confirmButtonClass: 'bg-red-600 hover:bg-red-700',
        onConfirm: doRemove
      });
    } else {
      doRemove();
    }
  };

  // Delete entire watchlist
  const handleDeleteWatchlist = async (watchlistId, watchlistName) => {
    const doDelete = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/watchlists/${watchlistId}?user_id=${user.id}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          // If we deleted the selected watchlist, select another one
          if (selectedWatchlist?.id === watchlistId) {
            const remaining = watchlists.filter(w => w.id !== watchlistId);
            setSelectedWatchlist(remaining.length > 0 ? remaining[0] : null);
          }
          await fetchWatchlists();
          if (showNotification) {
            showNotification(`Deleted "${watchlistName}"`, 'success');
          }
        } else {
          const error = await response.json();
          if (showNotification) {
            showNotification(error.detail || 'Failed to delete watchlist', 'error');
          }
        }
      } catch (err) {
        console.error('Error deleting watchlist:', err);
        if (showNotification) {
          showNotification('Error deleting watchlist', 'error');
        }
      }
    };

    // Use custom confirm modal if available
    if (onConfirmAction) {
      onConfirmAction({
        title: 'Delete Collection',
        message: `Are you sure you want to delete "${watchlistName}"? This cannot be undone.`,
        confirmText: 'Delete',
        confirmButtonClass: 'bg-red-600 hover:bg-red-700',
        onConfirm: doDelete
      });
    } else {
      // Fallback to window.confirm
      if (window.confirm(`Are you sure you want to delete "${watchlistName}"? This cannot be undone.`)) {
        doDelete();
      }
    }
  };

  // Render sort icon based on current sort config
  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" />
      : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />;
  };

  // Sort tickers within a category based on current sort config
  const getSortedCategoryTickers = (categoryTickers) => {
    if (!categoryTickers || categoryTickers.length === 0) return [];
    if (!sortConfig.key || sortConfig.key === 'ticker') {
      // Sort by ticker name
      const sorted = [...categoryTickers].sort((a, b) => {
        const strA = String(a).toLowerCase();
        const strB = String(b).toLowerCase();
        return sortConfig.direction === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
      });
      return sorted;
    }
    
    // Sort by financial metric - need to look up the data
    return [...categoryTickers].sort((a, b) => {
      const dataA = displayData.find(d => d.ticker?.toUpperCase() === a?.toUpperCase()) || {};
      const dataB = displayData.find(d => d.ticker?.toUpperCase() === b?.toUpperCase()) || {};
      return compareValues(dataA, dataB, sortConfig.key, sortConfig.direction);
    });
  };

  // Sort options for dropdown
  const sortOptions = [
    { key: 'ticker', label: 'Ticker' },
    { key: 'market_cap', label: 'MCap' },
    { key: 'enterprise_value', label: 'EV' },
    { key: 'forward_pe', label: 'P/E' },
    { key: 'ev_to_sales', label: 'EV/S' },
    { key: 'dividend_yield', label: 'Div' },
  ];

  const [showSortMenu, setShowSortMenu] = useState(false);
  const [watchlistAgeSortBy, setWatchlistAgeSortBy] = useState('newest'); // Age-based sort: newest, fresh, warm, stale

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.sort-dropdown-container')) {
        setShowSortMenu(false);
      }
      if (!e.target.closest('.filter-dropdown-container')) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Only show loading skeleton on initial load when we have no data yet
  if (loading && initialLoad && watchlists.length === 0) {
    return (
      <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <CardContent className="p-0">
          <div className="p-4 bg-slate-800 text-white">
            <div className="flex items-center justify-between">
              <div className="h-8 w-48 bg-slate-700 rounded animate-pulse"></div>
              <div className="h-8 w-24 bg-slate-700 rounded animate-pulse"></div>
            </div>
          </div>
          <div className="p-3 sm:p-4 flex items-center justify-center min-h-[200px]">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={isWorkspaceMode ? 'h-full flex flex-col' : ''}>
    <Card className={`border-0 shadow-none rounded-none ${isDarkMode ? 'bg-slate-900' : 'bg-white'} ${isWorkspaceMode ? 'h-full flex flex-col' : ''}`}>
      <CardContent className={`p-0 ${isWorkspaceMode ? 'h-full flex flex-col' : ''}`}>
        {/* Single-Row Header (like Dashboard) - Sticky in workspace mode */}
        <div className={`p-4 bg-slate-800 text-white ${isWorkspaceMode ? 'flex-shrink-0 sticky top-0 z-10' : ''}`}>
          {/* Mobile: Two rows | Desktop: Single row */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {/* Left side: My Watchlists / Received toggle */}
            <WatchlistViewTabs
              watchlistView={watchlistView}
              setWatchlistView={(view) => {
                setSelectedCollectionPoll(null);
                setWatchlistView(view);
              }}
              myWatchlistsCount={watchlists.length}
              receivedCount={sharedWatchlists.length + sharedPolls.length}
            />
            
            {/* Center: Keyboard shortcuts hint - Desktop only, hidden in workspace */}
            {!isWorkspaceMode && (
              <button
                onClick={() => onSearch?.()}
                className="hidden sm:flex items-center text-white/50 text-sm"
                title="View keyboard shortcuts"
              >
                <span>Press </span>
                <kbd className="mx-1 px-1.5 py-0.5 bg-white/10 rounded text-xs font-mono">?</kbd>
                <span> for shortcuts</span>
              </button>
            )}
            
            {/* Right side: Action Buttons - Grid on mobile, flex on desktop */}
            <div className={isWorkspaceMode ? "flex items-center gap-1" : "grid grid-cols-4 gap-1 sm:flex sm:items-center sm:gap-2"}>
                {/* Search Button - Always first/far left */}
                <button 
                  onClick={onSearch}
                  className={isWorkspaceMode 
                    ? "p-2 text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg" 
                    : "w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 border border-white/20 rounded-lg transition-all duration-200"}
                  title="Search"
                >
                  <svg className={isWorkspaceMode ? "w-4 h-4" : "w-4 h-4 sm:mr-2"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                  {!isWorkspaceMode && <span className="hidden sm:inline">Search</span>}
                </button>
                
                {/* Sort Button - Age-based sorting like Dashboard (disabled when viewing a specific watchlist) */}
                <div className="relative sort-dropdown-container">
                  <button
                    onClick={() => !selectedWatchlist && setShowSortMenu(!showSortMenu)}
                    disabled={selectedWatchlist && !viewAllMode}
                    className={isWorkspaceMode 
                      ? `p-2 border rounded-lg ${selectedWatchlist && !viewAllMode ? 'text-white/30 border-white/10 cursor-not-allowed' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`
                      : `w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-lg transition-all duration-200 ${selectedWatchlist && !viewAllMode ? 'text-white/30 border-white/10 cursor-not-allowed' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`}
                    title={selectedWatchlist && !viewAllMode ? 'Sort not available when viewing a watchlist' : 'Sort watchlists by age'}
                  >
                    <svg className={isWorkspaceMode ? "w-4 h-4" : "w-4 h-4 sm:mr-2"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                    {!isWorkspaceMode && <span className="hidden sm:inline">Sort</span>}
                  </button>
                  
                  {showSortMenu && !selectedWatchlist && (
                    <div className={`absolute left-0 sm:right-0 sm:left-auto mt-2 w-48 rounded-lg shadow-lg border z-50 ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'}`}>
                      <div className="py-1">
                        <button
                          onClick={() => { setWatchlistAgeSortBy('newest'); setShowSortMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${watchlistAgeSortBy === 'newest' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-slate-400 mr-2"></div>
                            All (newest first)
                          </div>
                        </button>
                        <button
                          onClick={() => { setWatchlistAgeSortBy('fresh'); setShowSortMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${watchlistAgeSortBy === 'fresh' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                            New (&lt; 3 days)
                          </div>
                        </button>
                        <button
                          onClick={() => { setWatchlistAgeSortBy('warm'); setShowSortMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${watchlistAgeSortBy === 'warm' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                            Recent (3-7 days)
                          </div>
                        </button>
                        <button
                          onClick={() => { setWatchlistAgeSortBy('stale'); setShowSortMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${watchlistAgeSortBy === 'stale' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                            Older (7+ days)
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Filter Button - Funnel icon to switch between watchlist types */}
                <div className="relative filter-dropdown-container">
                  <button
                    onClick={() => setShowFilterMenu(!showFilterMenu)}
                    className={isWorkspaceMode 
                      ? `p-2 border rounded-lg ${watchlistTypeFilter !== 'all' ? 'text-orange-500 border-orange-500 bg-orange-500/10' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`
                      : `w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border rounded-lg transition-all duration-200 ${watchlistTypeFilter !== 'all' ? 'text-orange-500 border-orange-500 bg-orange-500/10' : 'text-white/70 hover:text-white hover:bg-white/10 border-white/20'}`}
                    title="Filter by watchlist type"
                  >
                    <svg className={isWorkspaceMode ? "w-4 h-4" : "w-4 h-4 sm:mr-2"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    {!isWorkspaceMode && <span className="hidden sm:inline">Filter</span>}
                  </button>
                  
                  {showFilterMenu && (
                    <div className={`absolute left-0 sm:right-0 sm:left-auto mt-2 w-52 rounded-lg shadow-lg border z-50 ${isDarkMode ? 'bg-[#1a2235] border-slate-700' : 'bg-white border-gray-200'}`}>
                      <div className="py-1">
                        <button
                          onClick={() => { setWatchlistTypeFilter('all'); setShowFilterMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${watchlistTypeFilter === 'all' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                            </svg>
                            All Tickers
                          </div>
                          {watchlistTypeFilter === 'all' && (
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => { setWatchlistTypeFilter('ideas'); setShowFilterMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${watchlistTypeFilter === 'ideas' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            Curations
                          </div>
                          {watchlistTypeFilter === 'ideas' && (
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => { setWatchlistTypeFilter('sectors'); setShowFilterMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${watchlistTypeFilter === 'sectors' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            Sectors
                          </div>
                          {watchlistTypeFilter === 'sectors' && (
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => { setWatchlistTypeFilter('custom_lists'); setShowFilterMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${watchlistTypeFilter === 'custom_lists' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                            </svg>
                            Custom Lists
                          </div>
                          {watchlistTypeFilter === 'custom_lists' && (
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => { setWatchlistTypeFilter('requests'); setShowFilterMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${watchlistTypeFilter === 'requests' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            Requests
                          </div>
                          {watchlistTypeFilter === 'requests' && (
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => { setWatchlistTypeFilter('polls'); setShowFilterMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} ${watchlistTypeFilter === 'polls' ? (isDarkMode ? 'bg-blue-900/50 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium') : (isDarkMode ? 'text-slate-300' : 'text-gray-700')}`}
                        >
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                            </svg>
                            X Polls
                          </div>
                          {watchlistTypeFilter === 'polls' && (
                            <svg className={`w-4 h-4 ${isDarkMode ? 'text-blue-300' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              
              {/* New Button with Dropdown - Orange with Plus */}
              <div className="relative">
                <button 
                  onClick={() => setShowNewDropdown(!showNewDropdown)}
                  className={isWorkspaceMode 
                    ? "p-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                    : "w-full sm:w-auto inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200"}
                >
                  <Plus className={isWorkspaceMode ? "w-4 h-4" : "w-4 h-4 sm:mr-2"} />
                  {!isWorkspaceMode && <span className="hidden sm:inline">New</span>}
                  {!isWorkspaceMode && <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>}
                </button>
                
                {/* Dropdown Menu */}
                {showNewDropdown && (
                  <>
                    {/* Backdrop to close dropdown */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowNewDropdown(false)}
                    />
                    <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg z-50 py-1 ${
                      isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'
                    }`}>
                      {/* Custom List */}
                      <button
                        onClick={() => {
                          setShowNewDropdown(false);
                          setShowCreateWatchlist(true);
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 ${
                          isDarkMode 
                            ? 'text-slate-200 hover:bg-slate-700' 
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                        </svg>
                        Custom List
                      </button>
                      
                      {/* Requests (Ask for Ideas) */}
                      <button
                        onClick={() => {
                          setShowNewDropdown(false);
                          onCreatePoll?.('requests');
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 ${
                          isDarkMode 
                            ? 'text-slate-200 hover:bg-slate-700' 
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Requests
                      </button>
                      
                      {/* X Poll */}
                      <button
                        onClick={() => {
                          setShowNewDropdown(false);
                          onCreatePoll?.('x_poll');
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 ${
                          isDarkMode 
                            ? 'text-slate-200 hover:bg-slate-700' 
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                        </svg>
                        X Poll
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content Area - Scrollable in workspace mode */}
        <div className={`p-3 sm:p-4 ${isWorkspaceMode ? 'flex-1 overflow-y-auto' : ''}`}>
          {/* Collapse button to return to View All mode - at the top */}
          {selectedWatchlist && (
            <div className={`mb-4 flex items-center justify-start`}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedWatchlist(null);
                  setViewAllMode(true);
                }}
                className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white' 
                    : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-800 border border-slate-300'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
                <span>Back to All</span>
              </button>
            </div>
          )}

          {error && (
            <div className={`${isDarkMode ? 'bg-red-900/30 border-red-700 text-red-400' : 'bg-red-50 border-red-300 text-red-700'} border p-3 rounded-lg mb-4`}>
              {error}
            </div>
          )}

          {/* Poll View - shows when a poll is selected from Collections */}
          {selectedCollectionPoll ? (
            <div>
              {/* Back to All button */}
              <button
                onClick={() => setSelectedCollectionPoll(null)}
                className={`inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full border transition-colors text-sm font-medium ${
                  isDarkMode 
                    ? 'bg-slate-800 border-slate-600 text-white hover:bg-slate-700' 
                    : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="text-lg">«</span>
                Back to All
              </button>
              
              {/* Poll content */}
              <PollSpreadsheet
                key={`poll-${selectedCollectionPoll?.id}-${selectedCollectionPoll?.tickers_discovered?.length || 0}`}
                selectedPoll={selectedCollectionPoll}
                onTickerClick={onTickerClick}
                isDarkMode={isDarkMode}
                onOriginal={onOriginal}
                onDocuments={onDocuments}
                onSchedule={onSchedule}
                onShare={onShare}
                onWatchlist={onWatchlist}
                onPollUpdated={onPollUpdated}
                user={user}
                onAddTickers={async (pollId) => {
                  // Refresh the poll data after adding a ticker
                  try {
                    const userId = user?.id || user?.user_id;
                    const response = await fetch(`${API_BASE_URL}/api/fishing-threads/${userId}`);
                    if (response.ok) {
                      const data = await response.json();
                      // Update the selected poll with fresh data
                      const updatedPoll = data.threads?.find(p => p.id === pollId);
                      if (updatedPoll) {
                        setSelectedCollectionPoll(updatedPoll);
                      }
                      // Dispatch event to refresh polls in parent
                      window.dispatchEvent(new CustomEvent('refreshPolls', { detail: data.threads }));
                    }
                  } catch (error) {
                    console.error('Error refreshing poll:', error);
                  }
                }}
              />
            </div>
          ) : (
          <>
          {/* View All Mode - Accordion of all watchlists */}
          {viewAllMode && !selectedWatchlist && (
            <div className="space-y-3">
              {/* Show content based on watchlistView tab */}
              {watchlistView === 'my' ? (
                <>
                  {/* Curations Section */}
                  {(watchlistTypeFilter === 'all' || watchlistTypeFilter === 'ideas') && (
                    <div className={`rounded-lg border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                      <button 
                        onClick={() => toggleCategory('curations')}
                        className={`w-full px-4 py-4 text-sm font-bold uppercase tracking-wide flex items-center justify-between gap-3 ${isDarkMode ? 'text-slate-200 hover:bg-slate-700/50' : 'text-slate-700 hover:bg-slate-50'} rounded-t-lg transition-colors`}
                      >
                        <div className="flex items-center gap-3">
                          <svg className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                          </svg>
                          CURATIONS ({watchlists.filter(wl => {
                            if (!wl.source_info?.bookmark_id && !wl.source_info?.author_name) return false;
                            if (watchlistAgeSortBy === 'newest') return true;
                            const days = calculateDaysOld(wl.created_at || wl.updated_at);
                            if (watchlistAgeSortBy === 'fresh') return days < 3;
                            if (watchlistAgeSortBy === 'warm') return days >= 3 && days < 7;
                            if (watchlistAgeSortBy === 'stale') return days >= 7;
                            return true;
                          }).length})
                        </div>
                        <svg className={`w-6 h-6 transition-transform ${expandedCategories.has('curations') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      </button>
                      {expandedCategories.has('curations') && (
                      <div className={`space-y-2 p-2 border-t ${isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}>
                        {watchlists.filter(wl => wl.source_info?.bookmark_id || wl.source_info?.author_name).length === 0 ? (
                          <div className={`p-4 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            No curations yet. Import bookmarks to get started!
                          </div>
                        ) : (
                        watchlists
                          .filter(wl => {
                            if (!wl.source_info?.bookmark_id && !wl.source_info?.author_name) return false;
                            if (watchlistAgeSortBy === 'newest') return true;
                            const days = calculateDaysOld(wl.created_at || wl.updated_at);
                            if (watchlistAgeSortBy === 'fresh') return days < 3;
                            if (watchlistAgeSortBy === 'warm') return days >= 3 && days < 7;
                            if (watchlistAgeSortBy === 'stale') return days >= 7;
                            return true;
                          })
                          .sort((a, b) => new Date(b.created_at || b.updated_at || 0) - new Date(a.created_at || a.updated_at || 0))
                          .map(wl => {
                            const daysOld = calculateDaysOld(wl.created_at || wl.updated_at);
                            const ageCategory = daysOld < 3 ? 'New' : daysOld < 7 ? 'Recent' : 'Older';
                            const ageBorderColor = ageCategory === 'New' ? 'border-green-400' : ageCategory === 'Recent' ? 'border-orange-400' : 'border-red-400';
                            const ageBgColor = ageCategory === 'New' ? 'bg-green-100 text-green-800' : ageCategory === 'Recent' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                            // Status bar color matching Ideas tab
                            const statusBarColor = ageCategory === 'New' ? 'bg-emerald-500' : ageCategory === 'Recent' ? 'bg-amber-500' : 'bg-rose-500';
                            
                            return (
                      <div key={wl.id} className={`relative rounded-lg overflow-hidden border transition-all duration-200 hover:-translate-y-[1px] ${isDarkMode ? 'bg-[#1a2235] border-white/5 hover:border-white/10 hover:shadow-card-hover' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'}`}>
                        {/* Status Bar - Left Edge */}
                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusBarColor}`} />
                        {/* Card content - clickable to expand AND toggle summary */}
                        <div
                          onClick={() => {
                            const viewKey = `curation-${wl.id}`;
                            const isCurrentlyExpanded = expandedWatchlists[wl.id];
                            // Sync toggle state: collapse = summary mode, expand = off mode
                            if (isCurrentlyExpanded) {
                              // Collapsing: switch back to summary
                              setCurationViewMode(prev => ({ ...prev, [viewKey]: 'summary' }));
                            } else {
                              // Expanding: switch to off (full post)
                              setCurationViewMode(prev => ({ ...prev, [viewKey]: 'full' }));
                            }
                            toggleWatchlistExpansion(wl.id, wl);
                          }}
                          className={`w-full px-4 py-3 ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors text-left cursor-pointer`}
                        >
                          <div className="space-y-0.5">
                            {/* Row 1: X Logo + Profile pic + @author | Toggle | ticker count */}
                            {(() => {
                              const viewKey = `curation-${wl.id}`;
                              const isFullPost = curationViewMode[viewKey] === 'full';
                              const hasContent = wl.original_post_content || wl.description;
                              
                              return (
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                    {/* X Logo */}
                                    <svg className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                    {wl.source_info?.profile_image && (
                                      <img src={wl.source_info.profile_image} alt="" className="w-5 h-5 rounded-full flex-shrink-0 object-cover" />
                                    )}
                                    <span className={`text-xs truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                      @{wl.source_info?.author_name?.replace('@', '')}
                                    </span>
                                  </div>
                                  
                                  {/* Toggle - center */}
                                  <div className="flex-shrink-0" style={{ width: '85px' }}>
                                    {hasContent && (
                                      <div className="flex items-center gap-1.5">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCurationViewMode(prev => ({ ...prev, [viewKey]: isFullPost ? 'summary' : 'full' }));
                                          }}
                                          className={`relative w-8 h-4 rounded-full cursor-pointer transition-colors duration-200 bg-black`}
                                          data-testid={`curation-grok-toggle-${wl.id}`}
                                        >
                                          {/* Sliding Grok logo - always black/white per official Grok branding */}
                                          <div 
                                            className={`absolute top-0.5 w-3 h-3 transition-all duration-200 ease-in-out ${
                                              isFullPost ? 'left-[18px]' : 'left-0.5'
                                            }`}
                                          >
                                            <svg className="w-3 h-3" viewBox="0 0 512 509.641" fill="#000000">
                                              <path d="M115.612 0h280.776C459.975 0 512 52.026 512 115.612v278.416c0 63.587-52.025 115.613-115.612 115.613H115.612C52.026 509.641 0 457.615 0 394.028V115.612C0 52.026 52.026 0 115.612 0z"/>
                                              <path fill="#ffffff" d="M213.235 306.019l178.976-180.002v.169l51.695-51.763c-.924 1.32-1.86 2.605-2.785 3.89-39.281 54.164-58.46 80.649-43.07 146.922l-.09-.101c10.61 45.11-.744 95.137-37.398 131.836-46.216 46.306-120.167 56.611-181.063 14.928l42.462-19.675c38.863 15.278 81.392 8.57 111.947-22.03 30.566-30.6 37.432-75.159 22.065-112.252-2.92-7.025-11.67-8.795-17.792-4.263l-124.947 92.341zm-25.786 22.437l-.033.034L68.094 435.217c7.565-10.429 16.957-20.294 26.327-30.149 26.428-27.803 52.653-55.359 36.654-94.302-21.422-52.112-8.952-113.177 30.724-152.898 41.243-41.254 101.98-51.661 152.706-30.758 11.23 4.172 21.016 10.114 28.638 15.639l-42.359 19.584c-39.44-16.563-84.629-5.299-112.207 22.313-37.298 37.308-44.84 102.003-1.128 143.81z"/>
                                            </svg>
                                          </div>
                                        </button>
                                        <span className={`text-[10px] font-medium ${isDarkMode ? 'text-white' : 'text-black'}`}>
                                          {isFullPost ? 'original' : 'preview'}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Ticker count - right */}
                                  <span className={`text-xs flex-shrink-0 text-right ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} style={{ width: '60px' }}>
                                    {wl.tickers?.length || 0} tickers
                                  </span>
                                </div>
                              );
                            })()}
                            
                            {/* Row 2: Name (hidden when full post mode) | Arrow */}
                            {(() => {
                              const viewKey = `curation-${wl.id}`;
                              const isFullPost = curationViewMode[viewKey] === 'full';
                              
                              return (
                                <div className="flex items-center justify-between">
                                  {!isFullPost && (
                                    <div className={`font-medium ml-6 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{wl.name}</div>
                                  )}
                                  {isFullPost && <div className="flex-1" />}
                                  <svg className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${expandedWatchlists[wl.id] ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                  </svg>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        {/* Action buttons - Curations - Only show when expanded */}
                        {expandedWatchlists[wl.id] && (
                        <div className={`flex flex-wrap items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 justify-center border-t backdrop-blur-sm ${isDarkMode ? 'border-slate-700 bg-slate-900/60' : 'border-slate-200 bg-slate-50/80'}`}>
                          {/* Delete button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteWatchlist(wl.id, wl.name);
                            }}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                            title="Delete watchlist"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                            <span className="text-[10px] sm:text-xs">Delete</span>
                          </button>
                          {/* Share button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onShare?.({ 
                                ticker: wl.name, 
                                watchlistId: wl.id,
                                content: `Watchlist: ${wl.name}`,
                                shareType: 'watchlist',
                                tickerCount: wl.tickers?.length || 0
                              });
                            }}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                            title="Share watchlist"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                            </svg>
                            <span className="text-[10px] sm:text-xs">Share</span>
                          </button>
                          {/* Open button - Opens original content in modal */}
                          <button
                            data-testid={`curation-open-btn-${wl.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Open original content in modal - pass isIdeasList and bookmarkId to fetch full content
                              if (onOriginal) {
                                onOriginal({
                                  isIdeasList: true,
                                  isTwitterContent: true,
                                  platformSource: 'Twitter',
                                  bookmarkId: wl.source_info?.bookmark_id || wl.id,
                                  ticker: wl.name,
                                  content: wl.original_post_content || wl.description,
                                  author: wl.source_info?.author_name,
                                  sourceType: wl.source_info?.source_name === 'X' ? 'twitter' : wl.source_info?.source_name,
                                  sourceUrl: wl.source_info?.source_url,
                                  twitterMetadata: {
                                    authorName: wl.source_info?.author_display_name || wl.source_info?.author_name,
                                    username: wl.source_info?.author_name?.replace('@', ''),
                                    profileImageUrl: wl.source_info?.profile_image,
                                    text: wl.original_post_content || wl.description,
                                    createdAt: wl.source_info?.created_at,
                                    tweetUrl: wl.source_info?.source_url,
                                    metrics: wl.source_info?.metrics
                                  }
                                });
                              }
                            }}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                            title="View original content"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            <span className="text-[10px] sm:text-xs">Open</span>
                          </button>
                        </div>
                        )}
                        
                        {/* Original Post Content - Show when expanded */}
                        {expandedWatchlists[wl.id] && (wl.original_post_content || wl.description) && (
                          <div className={`px-4 py-3 border-t ${isDarkMode ? 'border-slate-700 bg-slate-800/30' : 'border-slate-200 bg-slate-50'}`}>
                            {/* Simple author header - reuse same data as collapsed header */}
                            {wl.source_info?.author_name && (
                              <div className="flex items-start gap-3 mb-3">
                                {/* Avatar */}
                                {wl.source_info?.profile_image ? (
                                  <a href={`https://x.com/${wl.source_info.author_name.replace('@', '')}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                    <img src={wl.source_info.profile_image} alt="" className="w-12 h-12 rounded-full object-cover hover:opacity-80" />
                                  </a>
                                ) : (
                                  <a href={`https://x.com/${wl.source_info.author_name.replace('@', '')}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center hover:opacity-80 ${isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}>
                                      <svg className={`w-7 h-7 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 12c2.7 0 4.875-2.175 4.875-4.875S14.7 2.25 12 2.25 7.125 4.425 7.125 7.125 9.3 12 12 12zm0 2.25c-3.262 0-9.75 1.637-9.75 4.875V21h19.5v-1.875c0-3.238-6.488-4.875-9.75-4.875z"/>
                                      </svg>
                                    </div>
                                  </a>
                                )}
                                
                                {/* Name + Username + X Logo */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <a href={`https://x.com/${wl.source_info.author_name.replace('@', '')}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`font-bold block hover:underline ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {wl.source_info.author_display_name || wl.source_info.author_name.replace('@', '')}
                                      </a>
                                      <a href={`https://x.com/${wl.source_info.author_name.replace('@', '')}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`block hover:underline ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                        @{wl.source_info.author_name.replace('@', '')}
                                      </a>
                                    </div>
                                    <svg className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Timestamp + View on X */}
                            <div className={`flex items-center gap-2 mb-3 ${wl.source_info?.author_name ? 'ml-[60px]' : ''}`}>
                              {(wl.source_info?.created_at || wl.created_at) && (
                                <a href={wl.source_info?.source_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`text-sm hover:underline ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                  {new Date(wl.source_info?.created_at || wl.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                </a>
                              )}
                              {wl.source_info?.source_url && (
                                <>
                                  {(wl.source_info?.created_at || wl.created_at) && <span className={isDarkMode ? 'text-slate-600' : 'text-gray-400'}>·</span>}
                                  <a href={wl.source_info.source_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-sm text-blue-500 hover:underline">
                                    View on X
                                  </a>
                                </>
                              )}
                            </div>
                            
                            {/* Post content with truncation - use original_post_content first, fallback to description */}
                            {(() => {
                              const postContent = wl.original_post_content || wl.description;
                              const contentKey = `curation-${wl.id}`;
                              const isLong = postContent.length > 280;
                              const isExpanded = expandedCurationContent[contentKey];
                              const displayContent = isExpanded ? postContent : postContent.substring(0, 280);
                              
                              return (
                                <div>
                                  <div className={`text-sm leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                    {renderTextWithLinks(decodeHtmlEntities(displayContent), isDarkMode)}{isLong && !isExpanded && '...'}
                                  </div>
                                  {isLong && !isExpanded && (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setExpandedCurationContent(prev => ({ ...prev, [contentKey]: true }));
                                      }}
                                      className={`mt-1 text-xs font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                                    >
                                      more...
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                            
                            {/* Engagement Metrics - BELOW the post content */}
                            {wl.source_info?.metrics && (
                              <div className={`flex items-center gap-4 mt-3 pt-2 text-sm border-t ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-slate-200'}`} style={{marginLeft: '60px'}}>
                                {(wl.source_info.metrics.reply_count > 0 || wl.source_info.metrics.replyCount > 0) && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                                    </svg>
                                    {(wl.source_info.metrics.reply_count || wl.source_info.metrics.replyCount || 0).toLocaleString()}
                                  </span>
                                )}
                                {(wl.source_info.metrics.retweet_count > 0 || wl.source_info.metrics.retweetCount > 0) && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                    </svg>
                                    {(wl.source_info.metrics.retweet_count || wl.source_info.metrics.retweetCount || 0).toLocaleString()}
                                  </span>
                                )}
                                {(wl.source_info.metrics.like_count > 0 || wl.source_info.metrics.likeCount > 0) && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                                    </svg>
                                    {(wl.source_info.metrics.like_count || wl.source_info.metrics.likeCount || 0).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Expanded tickers - Full categorized view with expandable rows */}
                        {expandedWatchlists[wl.id] && (
                          <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                            <div className={`${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50/50'}`}>
                              {(wl.tickers || []).length === 0 ? (
                                <p className={`px-3 py-4 text-sm text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No tickers in this list</p>
                              ) : (
                                <div className={`p-2 ${isDarkMode ? 'bg-slate-900' : ''}`}>
                                  {(wl.source_info?.categorized_tickers || []).length > 0 ? (
                                    <div className={`space-y-3 ${isDarkMode ? 'bg-slate-900' : ''}`}>
                                      {(wl.source_info?.categorized_tickers || []).map((category, catIdx) => (
                                        <div key={catIdx} className={`rounded-lg border ${isDarkMode ? 'border-slate-600 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                                          <div className={`px-3 py-2 font-semibold text-sm ${isDarkMode ? 'text-slate-300 bg-slate-700' : 'text-slate-700 bg-slate-100/50'} rounded-t-lg`}>
                                            {category.category || 'Uncategorized'} ({(category.tickers || []).length})
                                          </div>
                                          <div className="overflow-x-auto">
                                            <table className="w-full text-xs">
                                              <thead className={`${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                                                <tr>
                                                  <th className={`px-2 py-1.5 text-left font-medium sticky left-0 ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>Ticker</th>
                                                  <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>MCap</th>
                                                  <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV</th>
                                                  <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>P/E</th>
                                                  <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV/S</th>
                                                  <th className={`px-2 py-1.5 w-6`}></th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {(category.tickers || []).map((ticker, idx) => {
                                                  const tickerSymbol = typeof ticker === 'string' ? ticker : ticker.symbol || ticker;
                                                  const tickerMetrics = allTickersData.find(d => d.ticker?.toUpperCase() === tickerSymbol?.toUpperCase()) || {};
                                                  const rowKey = `cur-${wl.id}-${catIdx}-${tickerSymbol}`;
                                                  const isRowExp = expandedRowTicker === rowKey;
                                                  return (
                                                    <React.Fragment key={idx}>
                                                      <tr data-testid={`curation-ticker-row-${tickerSymbol}`}
                                                        className={`border-t cursor-pointer ${isDarkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-200/50 hover:bg-slate-100/50'} ${isRowExp ? (isDarkMode ? 'bg-slate-700/50' : 'bg-slate-100') : ''}`}
                                                        onClick={() => setExpandedRowTicker(prev => prev === rowKey ? null : rowKey)}>
                                                        <td className={`px-2 py-1.5 font-mono font-bold sticky left-0 ${isDarkMode ? 'bg-slate-800/50 text-blue-400' : 'bg-slate-50/50 text-blue-600'}`}>${tickerSymbol}</td>
                                                        <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.market_cap, 'currency')}</td>
                                                        <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.enterprise_value, 'currency')}</td>
                                                        <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.forward_pe, 'ratio')}</td>
                                                        <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.ev_to_sales, 'ratio')}</td>
                                                        <td className="px-1 py-1 text-center"><svg className={`w-4 h-4 transform transition-transform ${isRowExp ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg></td>
                                                      </tr>
                                                      {isRowExp && (
                                                        <tr><td colSpan={6} className="p-0">
                                                          <div className={`px-2 py-2 border-t ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                                            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                                              {/* Folder */}
                                                              <button 
                                                                onClick={(e) => { e.stopPropagation(); if (onSchedule) onSchedule({ ticker: tickerSymbol, bookmarkId: wl.source_info?.bookmark_id || wl.id }); }} 
                                                                className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-purple-400 hover:bg-purple-500/10' : 'text-purple-600 hover:bg-purple-50'}`}
                                                              >
                                                                <FolderIcon className="w-5 h-5" />
                                                                <span className="text-[10px]">Folder</span>
                                                              </button>
                                                              {/* File */}
                                                              <button 
                                                                onClick={(e) => { e.stopPropagation(); if (onDocuments) onDocuments({ ticker: tickerSymbol, bookmarkId: wl.source_info?.bookmark_id || wl.id }); }} 
                                                                className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-slate-400 hover:bg-slate-500/10' : 'text-slate-600 hover:bg-slate-100'}`}
                                                              >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                                                <span className="text-[10px]">File</span>
                                                              </button>
                                                              {/* Ticker */}
                                                              <button 
                                                                onClick={(e) => { e.stopPropagation(); if (onWatchlist) onWatchlist(tickerSymbol); }} 
                                                                className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-green-400 hover:bg-green-500/10' : 'text-green-600 hover:bg-green-50'}`}
                                                              >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                                                <span className="text-[10px]">Ticker</span>
                                                              </button>
                                                              {/* Share */}
                                                              <button 
                                                                onClick={(e) => { e.stopPropagation(); if (onShare) onShare({ ticker: tickerSymbol, bookmarkId: wl.source_info?.bookmark_id || wl.id, content: wl.description }); }} 
                                                                className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-yellow-400 hover:bg-yellow-500/10' : 'text-yellow-600 hover:bg-yellow-50'}`}
                                                              >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/></svg>
                                                                <span className="text-[10px]">Share</span>
                                                              </button>
                                                            </div>
                                                          </div>
                                                        </td></tr>
                                                      )}
                                                    </React.Fragment>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead className={`${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                                          <tr>
                                            <th className={`px-2 py-1.5 text-left font-medium sticky left-0 ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>Ticker</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>MCap</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>P/E</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV/S</th>
                                            <th className={`px-2 py-1.5 w-6`}></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(wl.tickers || []).map((ticker, idx) => {
                                            const tickerSymbol = typeof ticker === 'string' ? ticker : ticker.symbol || ticker;
                                            const tickerMetrics = allTickersData.find(d => d.ticker?.toUpperCase() === tickerSymbol?.toUpperCase()) || {};
                                            const rowKey = `cur-${wl.id}-f-${tickerSymbol}`;
                                            const isRowExp = expandedRowTicker === rowKey;
                                            return (
                                              <React.Fragment key={idx}>
                                                <tr className={`border-t cursor-pointer ${isDarkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-200/50 hover:bg-slate-100/50'} ${isRowExp ? (isDarkMode ? 'bg-slate-700/50' : 'bg-slate-100') : ''}`}
                                                  onClick={() => setExpandedRowTicker(prev => prev === rowKey ? null : rowKey)}>
                                                  <td className={`px-2 py-1.5 font-mono font-bold sticky left-0 ${isDarkMode ? 'bg-slate-800/50 text-blue-400' : 'bg-slate-50/50 text-blue-600'}`}>${tickerSymbol}</td>
                                                  <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.market_cap, 'currency')}</td>
                                                  <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.enterprise_value, 'currency')}</td>
                                                  <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.forward_pe, 'ratio')}</td>
                                                  <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.ev_to_sales, 'ratio')}</td>
                                                  <td className="px-1 py-1 text-center"><svg className={`w-4 h-4 transform transition-transform ${isRowExp ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg></td>
                                                </tr>
                                                {isRowExp && (
                                                  <tr><td colSpan={6} className="p-0">
                                                    <div className={`px-2 py-2 border-t ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                                      <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                                        {/* Folder */}
                                                        <button 
                                                          onClick={(e) => { e.stopPropagation(); if (onSchedule) onSchedule({ ticker: tickerSymbol, bookmarkId: wl.id }); }} 
                                                          className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-purple-400 hover:bg-purple-500/10' : 'text-purple-600 hover:bg-purple-50'}`}
                                                        >
                                                          <FolderIcon className="w-5 h-5" />
                                                          <span className="text-[10px]">Folder</span>
                                                        </button>
                                                        {/* File */}
                                                        <button 
                                                          onClick={(e) => { e.stopPropagation(); if (onDocuments) onDocuments({ ticker: tickerSymbol, bookmarkId: wl.id }); }} 
                                                          className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-slate-400 hover:bg-slate-500/10' : 'text-slate-600 hover:bg-slate-100'}`}
                                                        >
                                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                                          <span className="text-[10px]">File</span>
                                                        </button>
                                                        {/* Ticker */}
                                                        <button 
                                                          onClick={(e) => { e.stopPropagation(); if (onWatchlist) onWatchlist(tickerSymbol); }} 
                                                          className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-green-400 hover:bg-green-500/10' : 'text-green-600 hover:bg-green-50'}`}
                                                        >
                                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                                          <span className="text-[10px]">Ticker</span>
                                                        </button>
                                                        {/* Share */}
                                                        <button 
                                                          onClick={(e) => { e.stopPropagation(); if (onShare) onShare({ ticker: tickerSymbol, bookmarkId: wl.id, content: wl.description }); }} 
                                                          className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-yellow-400 hover:bg-yellow-500/10' : 'text-yellow-600 hover:bg-yellow-50'}`}
                                                        >
                                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/></svg>
                                                          <span className="text-[10px]">Share</span>
                                                        </button>
                                                      </div>
                                                    </div>
                                                  </td></tr>
                                                )}
                                              </React.Fragment>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                    })
                        )}
                  </div>
                      )}
                </div>
              )}
              
              {/* Sector Watchlists */}
              {(watchlistTypeFilter === 'all' || watchlistTypeFilter === 'sectors') && (
                <div className={`rounded-lg border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                  <button 
                    onClick={() => toggleCategory('sectors')}
                    className={`w-full px-4 py-4 text-sm font-bold uppercase tracking-wide flex items-center justify-between gap-3 ${isDarkMode ? 'text-slate-200 hover:bg-slate-700/50' : 'text-slate-700 hover:bg-slate-50'} rounded-t-lg transition-colors`}
                  >
                    <div className="flex items-center gap-3">
                      <svg className={`w-6 h-6 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
                      </svg>
                      SECTORS ({watchlists.filter(wl => {
                        if (wl.source_info) return false;
                        if (!['Technology', 'Healthcare', 'Energy', 'Consumer Cyclical', 'Consumer Defensive', 'Industrials', 'General', 'Financial Services', 'Communication Services', 'Real Estate', 'Basic Materials', 'Utilities'].includes(wl.name)) return false;
                        if (watchlistAgeSortBy === 'newest') return true;
                        const days = calculateDaysOld(wl.created_at || wl.updated_at);
                        if (watchlistAgeSortBy === 'fresh') return days < 3;
                        if (watchlistAgeSortBy === 'warm') return days >= 3 && days < 7;
                        if (watchlistAgeSortBy === 'stale') return days >= 7;
                        return true;
                      }).length})
                    </div>
                    <svg className={`w-6 h-6 transition-transform ${expandedCategories.has('sectors') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                  {expandedCategories.has('sectors') && (
                  <div className={`space-y-2 p-2 border-t ${isDarkMode ? 'border-slate-700 bg-white' : 'border-slate-200'}`}>
                    {watchlists.filter(wl => {
                      if (wl.source_info) return false;
                      return ['Technology', 'Healthcare', 'Energy', 'Consumer Cyclical', 'Consumer Defensive', 'Industrials', 'General', 'Financial Services', 'Communication Services', 'Real Estate', 'Basic Materials', 'Utilities'].includes(wl.name);
                    }).length === 0 ? (
                      <div className={`p-4 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        No sector watchlists yet.
                      </div>
                    ) : (
                    watchlists
                      .filter(wl => {
                        if (wl.source_info) return false;
                        if (!['Technology', 'Healthcare', 'Energy', 'Consumer Cyclical', 'Consumer Defensive', 'Industrials', 'General', 'Financial Services', 'Communication Services', 'Real Estate', 'Basic Materials', 'Utilities'].includes(wl.name)) return false;
                        if (watchlistAgeSortBy === 'newest') return true;
                        const days = calculateDaysOld(wl.created_at || wl.updated_at);
                        if (watchlistAgeSortBy === 'fresh') return days < 3;
                        if (watchlistAgeSortBy === 'warm') return days >= 3 && days < 7;
                        if (watchlistAgeSortBy === 'stale') return days >= 7;
                        return true;
                      })
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map(wl => {
                        return (
                      <div key={wl.id} className={`rounded-lg border ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'} overflow-hidden`}>
                        {/* Row 1: Name + down arrow */}
                        <button
                          onClick={() => toggleWatchlistExpansion(wl.id, wl)}
                          className={`w-full px-4 py-3 flex items-start justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors text-left`}
                        >
                          <div className={`font-medium flex-1 min-w-0 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{wl.name}</div>
                          <svg className={`w-5 h-5 flex-shrink-0 mt-1 transition-transform duration-200 ${expandedWatchlists[wl.id] ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                          </svg>
                        </button>
                        {/* Row 2: Ticker count */}
                        <div className={`px-4 py-2`}>
                          <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{wl.tickers?.length || 0} tickers</div>
                        </div>
                        {/* Row 3: Action buttons - Sectors - Only show when expanded */}
                        {expandedWatchlists[wl.id] && (
                        <div className={`flex flex-wrap items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 justify-center border-t backdrop-blur-sm ${isDarkMode ? 'border-slate-700 bg-slate-900/60' : 'border-slate-200 bg-slate-50/80'}`}>
                          {/* Add Ideas button - First on left */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedWatchlist(wl);
                              setShowAddToWatchlist(true);
                            }}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                            title="Add ideas to sector"
                          >
                            <Plus className="w-6 h-6" />
                            <span className="text-[10px] sm:text-xs">Add</span>
                          </button>
                          {/* Delete button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteWatchlist(wl.id, wl.name);
                            }}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                            title="Delete watchlist"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                            <span className="text-[10px] sm:text-xs">Delete</span>
                          </button>
                          {/* Share button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onShare?.({ 
                                ticker: wl.name, 
                                watchlistId: wl.id,
                                content: `Watchlist: ${wl.name}`,
                                shareType: 'watchlist',
                                tickerCount: wl.tickers?.length || 0
                              });
                            }}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                            title="Share watchlist"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                            </svg>
                            <span className="text-[10px] sm:text-xs">Share</span>
                          </button>
                          {/* Open button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewAllMode(false);
                              setSelectedWatchlist(wl);
                            }}
                            className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                            title="Open sector"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span className="text-[10px] sm:text-xs">Open</span>
                          </button>
                        </div>
                        )}
                        {/* Expanded tickers spreadsheet */}
                        {expandedWatchlists[wl.id] && (
                          <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                            <div className={`${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50/50'}`}>
                              {(wl.tickers || []).length === 0 ? (
                                <p className={`px-3 py-4 text-sm text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No tickers in this list</p>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead className={`${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                                      <tr>
                                        <th className={`px-2 py-1.5 text-left font-medium sticky left-0 ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>Ticker</th>
                                        <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Mkt Cap</th>
                                        <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV</th>
                                        <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Fwd P/E</th>
                                        <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV/Sales</th>
                                        <th className={`px-2 py-1.5 text-center font-medium hidden sm:table-cell ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>P/B</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(wl.tickers || []).map((ticker, idx) => {
                                        const tickerSymbol = typeof ticker === 'string' ? ticker : ticker.symbol || ticker;
                                        const tickerMetrics = allTickersData.find(d => d.ticker?.toUpperCase() === tickerSymbol?.toUpperCase()) || {};
                                        return (
                                          <tr key={`${wl.id || wl.name}-${tickerSymbol}-${idx}`} className={`border-t ${isDarkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-200/50 hover:bg-slate-100/50'}`}>
                                            <td className={`px-2 py-1.5 font-mono font-bold sticky left-0 ${isDarkMode ? 'bg-slate-800/50 text-blue-400' : 'bg-slate-50/50 text-blue-600'}`}>${tickerSymbol}</td>
                                            <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.market_cap, 'currency')}</td>
                                            <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.enterprise_value, 'currency')}</td>
                                            <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.forward_pe, 'ratio')}</td>
                                            <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.ev_to_sales, 'ratio')}</td>
                                            <td className={`px-2 py-1.5 text-center font-mono hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.price_to_book, 'ratio')}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                    })
                    )}
                  </div>
                  )}
                </div>
              )}
              
              {/* Custom Lists Section - Watchlists with list_type === 'custom_list' */}
              {(watchlistTypeFilter === 'all' || watchlistTypeFilter === 'custom_lists') && (
                <div className={`rounded-lg border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                  <button 
                    onClick={() => toggleCategory('custom_lists')}
                    className={`w-full px-4 py-4 text-sm font-bold uppercase tracking-wide flex items-center justify-between gap-3 ${isDarkMode ? 'text-slate-200 hover:bg-slate-700/50' : 'text-slate-700 hover:bg-slate-50'} rounded-t-lg transition-colors`}
                  >
                    <div className="flex items-center gap-3">
                      <svg className={`w-6 h-6 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                      </svg>
                      CUSTOM LISTS ({watchlists.filter(wl => wl.list_type === 'custom_list').length})
                    </div>
                    <svg className={`w-6 h-6 transition-transform ${expandedCategories.has('custom_lists') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                  {expandedCategories.has('custom_lists') && (
                  <div className={`space-y-2 p-2 border-t ${isDarkMode ? 'border-slate-700 bg-white' : 'border-slate-200'}`}>
                    {watchlists.filter(wl => wl.list_type === 'custom_list').length === 0 ? (
                      <div className={`p-4 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        No custom lists yet. Create one to track specific tickers!
                      </div>
                    ) : (
                    watchlists
                      .filter(wl => wl.list_type === 'custom_list')
                      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                      .map(list => {
                        const daysOld = calculateDaysOld(list.created_at);
                        const ageCategory = daysOld < 10 ? 'New' : daysOld <= 30 ? 'Recent' : 'Older';
                        const ageBorderColor = ageCategory === 'New' ? 'border-green-400' : ageCategory === 'Recent' ? 'border-orange-400' : 'border-red-400';
                        const ageBgColor = ageCategory === 'New' ? 'bg-green-100 text-green-800' : ageCategory === 'Recent' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                        // Status bar color matching Ideas tab
                        const statusBarColor = ageCategory === 'New' ? 'bg-emerald-500' : ageCategory === 'Recent' ? 'bg-amber-500' : 'bg-rose-500';
                        
                        return (
                          <div key={list.id} className={`relative rounded-lg overflow-hidden border transition-all duration-200 hover:-translate-y-[1px] ${isDarkMode ? 'bg-[#1a2235] border-white/5 hover:border-white/10 hover:shadow-card-hover' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'}`}>
                            {/* Status Bar - Left Edge */}
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusBarColor}`} />
                            {/* Row 1: Name + down arrow */}
                            <button
                              onClick={() => toggleWatchlistExpansion(list.id, list)}
                              className={`w-full px-4 py-3 flex items-start justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors text-left`}
                            >
                              <div className={`font-medium flex-1 min-w-0 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{list.name}</div>
                              <svg className={`w-5 h-5 flex-shrink-0 mt-1 transition-transform duration-200 ${expandedWatchlists[list.id] ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                              </svg>
                            </button>
                            {/* Row 2: Ticker count + age badge */}
                            <div className={`px-4 py-2 flex items-center justify-between`}>
                              <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {list.tickers?.length || 0} tickers
                              </div>
                            </div>
                            {/* Row 3: Action buttons - Only show when expanded */}
                            {expandedWatchlists[list.id] && (
                            <div className={`flex flex-wrap items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 justify-center border-t backdrop-blur-sm ${isDarkMode ? 'border-slate-700 bg-slate-900/60' : 'border-slate-200 bg-slate-50/80'}`}>
                              {/* Add Ideas button - First on left */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedWatchlist(list);
                                  setShowAddToWatchlist(true);
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                                title="Add ideas to list"
                              >
                                <Plus className="w-6 h-6" />
                                <span className="text-[10px] sm:text-xs">Add</span>
                              </button>
                              {/* Delete button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteWatchlist(list.id, list.name);
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                                title="Delete list"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Delete</span>
                              </button>
                              {/* Share button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onShare?.({
                                    ticker: list.name,
                                    bookmarkId: list.id,
                                    content: `Custom List: ${list.name}`,
                                    shareType: 'watchlist',
                                    tickerCount: list.tickers?.length || 0
                                  });
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}`}
                                title="Share list"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Share</span>
                              </button>
                              {/* Open button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedWatchlist(list);
                                  setSelectedCollectionPoll(null);
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                                title="Open list"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span className="text-[10px] sm:text-xs">Open</span>
                              </button>
                            </div>
                            )}
                            {/* Expanded tickers spreadsheet */}
                            {expandedWatchlists[list.id] && (
                              <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                <div className={`${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50/50'}`}>
                                  {(list.tickers || []).length === 0 ? (
                                    <p className={`px-3 py-4 text-sm text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No tickers in this list</p>
                                  ) : (
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead className={`${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                                          <tr>
                                            <th className={`px-2 py-1.5 text-left font-medium sticky left-0 ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>Ticker</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Mkt Cap</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Fwd P/E</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV/Sales</th>
                                            <th className={`px-2 py-1.5 text-center font-medium hidden sm:table-cell ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>P/B</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(list.tickers || []).map((ticker, idx) => {
                                            const tickerSymbol = typeof ticker === 'string' ? ticker : ticker.symbol || ticker;
                                            const tickerMetrics = allTickersData.find(d => d.ticker?.toUpperCase() === tickerSymbol?.toUpperCase()) || {};
                                            return (
                                              <tr key={`${list.id || list.name}-${tickerSymbol}-${idx}`} className={`border-t ${isDarkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-200/50 hover:bg-slate-100/50'}`}>
                                                <td className={`px-2 py-1.5 font-mono font-bold sticky left-0 ${isDarkMode ? 'bg-slate-800/50 text-blue-400' : 'bg-slate-50/50 text-blue-600'}`}>${tickerSymbol}</td>
                                                <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.market_cap, 'currency')}</td>
                                                <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.enterprise_value, 'currency')}</td>
                                                <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.forward_pe, 'ratio')}</td>
                                                <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.ev_to_sales, 'ratio')}</td>
                                                <td className={`px-2 py-1.5 text-center font-mono hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.price_to_book, 'ratio')}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  )}
                </div>
              )}
              
              {/* Requests Section - Ask for Ideas (source === 'requests' OR old polls without poll options) */}
              {(watchlistTypeFilter === 'all' || watchlistTypeFilter === 'requests') && (
                <div className={`rounded-lg border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                  <button 
                    onClick={() => toggleCategory('requests')}
                    className={`w-full px-4 py-4 text-sm font-bold uppercase tracking-wide flex items-center justify-between gap-3 ${isDarkMode ? 'text-slate-200 hover:bg-slate-700/50' : 'text-slate-700 hover:bg-slate-50'} rounded-t-lg transition-colors`}
                  >
                    <div className="flex items-center gap-3">
                      <svg className={`w-6 h-6 ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      REQUESTS ({polls.filter(p => {
                        if (!p.is_own_poll) return false;
                        // Requests: source is 'requests' or 'targeted_search' OR old polls without poll options pattern
                        const isRequest = p.source === 'requests' || p.source === 'targeted_search' ||
                          (p.source !== 'x_poll' && p.source !== 'custom_list' && 
                           !(/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || '')));
                        return isRequest;
                      }).length})
                    </div>
                    <svg className={`w-6 h-6 transition-transform ${expandedCategories.has('requests') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                  {expandedCategories.has('requests') && (
                  <div className={`space-y-2 p-2 border-t ${isDarkMode ? 'border-slate-700 bg-white' : 'border-slate-200'}`}>
                    {polls.filter(p => {
                      if (!p.is_own_poll) return false;
                      const isRequest = p.source === 'requests' || p.source === 'targeted_search' ||
                        (p.source !== 'x_poll' && p.source !== 'custom_list' && 
                         !(/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || '')));
                      return isRequest;
                    }).length === 0 ? (
                      <div className={`p-4 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        No requests yet. Create one to ask your network for ideas!
                      </div>
                    ) : (
                    polls
                      .filter(p => {
                        if (!p.is_own_poll) return false;
                        // Requests: source is 'requests' or 'targeted_search' OR old polls without poll options pattern
                        const isRequest = p.source === 'requests' || p.source === 'targeted_search' ||
                          (p.source !== 'x_poll' && p.source !== 'custom_list' && 
                           !(/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || '')));
                        return isRequest;
                      })
                      .sort((a, b) => new Date(b.created_at || b.updated_at || 0) - new Date(a.created_at || a.updated_at || 0))
                      .map(poll => {
                        const daysOld = calculateDaysOld(poll.created_at || poll.updated_at);
                        const ageCategory = daysOld < 10 ? 'New' : daysOld <= 30 ? 'Recent' : 'Older';
                        const ageBorderColor = ageCategory === 'New' ? 'border-green-400' : ageCategory === 'Recent' ? 'border-orange-400' : 'border-red-400';
                        const ageBgColor = ageCategory === 'New' ? 'bg-green-100 text-green-800' : ageCategory === 'Recent' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                        // Status bar color for Requests section
                        const statusBarColor = ageCategory === 'New' ? 'bg-emerald-500' : ageCategory === 'Recent' ? 'bg-amber-500' : 'bg-rose-500';
                        
                        return (
                          <div key={poll.id} className={`relative rounded-lg overflow-hidden border transition-all duration-200 hover:-translate-y-[1px] ${isDarkMode ? 'bg-[#1a2235] border-white/5 hover:border-white/10 hover:shadow-card-hover' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'}`}>
                            {/* Status Bar - Left Edge */}
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusBarColor}`} />
                            {/* Card content - clickable to expand */}
                            <button
                              onClick={() => {
                                const viewKey = `request-${poll.id}`;
                                const willExpand = !expandedWatchlists[`request_${poll.id}`];
                                
                                // Toggle expansion
                                toggleWatchlistExpansion(`request_${poll.id}`, { tickers: poll.tickers_discovered || poll.tickers || poll.all_tickers || [] });
                                
                                // Sync toggle state with expansion
                                if (willExpand) {
                                  // Expanding: switch to full post mode
                                  setRequestViewMode(prev => ({ ...prev, [viewKey]: 'full' }));
                                } else {
                                  // Collapsing: switch back to summary
                                  setRequestViewMode(prev => ({ ...prev, [viewKey]: 'summary' }));
                                }
                              }}
                              className={`w-full px-4 py-3 ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors text-left`}
                            >
                              <div className="space-y-0.5">
                                {/* Row 1: X Logo + Profile pic + @author | Toggle | stats - matching Curations */}
                                {(() => {
                                  const viewKey = `request-${poll.id}`;
                                  const isFullPost = requestViewMode[viewKey] === 'full';
                                  const hasContent = poll.original_tweet?.content || poll.content || poll.description;
                                  
                                  return (
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                    {/* X Logo */}
                                    {(poll.original_tweet?.url?.includes('x.com') || poll.original_tweet?.url?.includes('twitter.com') || poll.source_url?.includes('x.com') || poll.source_url?.includes('twitter.com') || poll.tweet_id) && (
                                      <svg className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                      </svg>
                                    )}
                                    {/* Profile pic */}
                                    {(poll.original_tweet?.author_profile_image || poll.profile_image_url || poll.author_profile_image) && (
                                      <img src={poll.original_tweet?.author_profile_image || poll.profile_image_url || poll.author_profile_image} alt="" className="w-5 h-5 rounded-full flex-shrink-0 object-cover" />
                                    )}
                                    {/* @username */}
                                    <span className={`text-xs truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                      @{(poll.original_tweet?.author_username || poll.author_username || poll.author_name || '').replace('@', '')}
                                    </span>
                                  </div>
                                  
                                  {/* Toggle - center */}
                                  <div className="flex-shrink-0" style={{ width: '85px' }}>
                                    {hasContent && (
                                      <div className="flex items-center gap-1.5">
                                        <div
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setRequestViewMode(prev => ({ ...prev, [viewKey]: isFullPost ? 'summary' : 'full' }));
                                          }}
                                          className={`relative w-8 h-4 rounded-full cursor-pointer transition-colors duration-200 bg-black`}
                                          data-testid={`request-grok-toggle-${poll.id}`}
                                        >
                                          {/* Sliding Grok logo */}
                                          <div 
                                            className={`absolute top-0.5 w-3 h-3 transition-all duration-200 ease-in-out ${
                                              isFullPost ? 'left-[18px]' : 'left-0.5'
                                            }`}
                                          >
                                            <svg className="w-3 h-3" viewBox="0 0 512 509.641" fill="#000000">
                                              <path d="M115.612 0h280.776C459.975 0 512 52.026 512 115.612v278.416c0 63.587-52.025 115.613-115.612 115.613H115.612C52.026 509.641 0 457.615 0 394.028V115.612C0 52.026 52.026 0 115.612 0z"/>
                                              <path fill="#ffffff" d="M213.235 306.019l178.976-180.002v.169l51.695-51.763c-.924 1.32-1.86 2.605-2.785 3.89-39.281 54.164-58.46 80.649-43.07 146.922l-.09-.101c10.61 45.11-.744 95.137-37.398 131.836-46.216 46.306-120.167 56.611-181.063 14.928l42.462-19.675c38.863 15.278 81.392 8.57 111.947-22.03 30.566-30.6 37.432-75.159 22.065-112.252-2.92-7.025-11.67-8.795-17.792-4.263l-124.947 92.341zm-25.786 22.437l-.033.034L68.094 435.217c7.565-10.429 16.957-20.294 26.327-30.149 26.428-27.803 52.653-55.359 36.654-94.302-21.422-52.112-8.952-113.177 30.724-152.898 41.243-41.254 101.98-51.661 152.706-30.758 11.23 4.172 21.016 10.114 28.638 15.639l-42.359 19.584c-39.44-16.563-84.629-5.299-112.207 22.313-37.298 37.308-44.84 102.003-1.128 143.81z"/>
                                            </svg>
                                          </div>
                                        </div>
                                        <span className={`text-[10px] font-medium ${isDarkMode ? 'text-white' : 'text-black'}`}>
                                          {isFullPost ? 'original' : 'preview'}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Stats - right */}
                                  <span className={`text-xs flex-shrink-0 text-right ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {poll.ticker_count || 0} tickers
                                  </span>
                                </div>
                                  );
                                })()}
                                
                                {/* Row 2: Title/Summary (hidden when full post mode) | Arrow - matching Curations */}
                                {(() => {
                                  const viewKey = `request-${poll.id}`;
                                  const isFullPost = requestViewMode[viewKey] === 'full';
                                  // Use preview/name for summary (like Curations), fallback to title
                                  const summaryText = poll.preview || poll.name || poll.title || poll.original_tweet?.content?.substring(0, 100) || '';
                                  
                                  return (
                                    <div className="flex items-center justify-between">
                                      {!isFullPost && summaryText && (
                                        <div className={`font-medium ml-6 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{summaryText}</div>
                                      )}
                                      {isFullPost && <div className="flex-1" />}
                                      <svg className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${expandedWatchlists[`request_${poll.id}`] ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                      </svg>
                                    </div>
                                  );
                                })()}
                              </div>
                            </button>
                            {/* Action buttons - Only show when expanded */}
                            {expandedWatchlists[`request_${poll.id}`] && (
                            <div className={`flex flex-wrap items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 justify-center border-t backdrop-blur-sm ${isDarkMode ? 'border-slate-700 bg-slate-900/60' : 'border-slate-200 bg-slate-50/80'}`}>
                              {/* Refresh button */}
                              <button
                                data-poll-id={poll.id}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const btn = e.currentTarget;
                                  const pollId = btn.getAttribute('data-poll-id');
                                  if (!pollId) {
                                    showNotification?.('Unable to refresh - poll ID not found', 'error');
                                    return;
                                  }
                                  try {
                                    btn.disabled = true;
                                    btn.querySelector('svg').classList.add('animate-spin');
                                    const response = await fetch(`${API_BASE_URL}/api/fishing-threads/${pollId}/fetch-replies`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ user_id: user?.id || user?.user_id || user?.email })
                                    });
                                    const result = await response.json();
                                    if (result.success) {
                                      const newCount = result.new_tickers || 0;
                                      const existingCount = result.already_known || 0;
                                      const totalReplies = result.replies_found || 0;
                                      let msg = `Refresh complete: ${totalReplies} replies`;
                                      if (newCount > 0) msg += `, ${newCount} new tickers`;
                                      if (existingCount > 0) msg += `, ${existingCount} already in list`;
                                      showNotification?.(msg, 'success');
                                      // Update the poll data locally with all details
                                      const updates = {
                                        reply_count: totalReplies,
                                        ticker_count: result.tickers_found?.length || 0,
                                        tickers_discovered: result.tickers_found || [],
                                        ticker_details: result.ticker_details || {}
                                      };
                                      onPollUpdated?.(pollId, updates);
                                      // Also update selectedCollectionPoll if viewing this poll
                                      if (selectedCollectionPoll?.id === pollId) {
                                        setSelectedCollectionPoll(prev => ({ ...prev, ...updates }));
                                      }
                                    } else {
                                      showNotification?.(result.error || 'Failed to refresh replies', 'error');
                                    }
                                  } catch (err) {
                                    console.error('Error refreshing request:', err);
                                    showNotification?.('Failed to fetch thread replies. Please try again.', 'error');
                                  } finally {
                                    btn.disabled = false;
                                    btn.querySelector('svg')?.classList.remove('animate-spin');
                                  }
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                                title="Refresh request replies"
                                data-testid={`request-refresh-btn-${poll.id}`}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Refresh</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeletePoll?.(poll.id, poll.title);
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                                title="Delete request"
                                data-testid={`request-delete-btn-${poll.id}`}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Delete</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onShare?.({
                                    ticker: poll.title,
                                    bookmarkId: poll.id,
                                    content: `Request: ${poll.title}`,
                                    shareType: 'poll',
                                    tickerCount: poll.ticker_count || 0,
                                    pollId: poll.id
                                  });
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                                title="Share request"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Share</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Open original content in modal - like Curations (Requests)
                                  if (onOriginal) {
                                    onOriginal({
                                      isIdeasList: true,
                                      isTwitterContent: true,
                                      platformSource: 'Twitter',
                                      bookmarkId: poll.tweet_id || poll.id,
                                      ticker: poll.title,
                                      content: poll.original_tweet?.text || poll.original_tweet?.content || poll.tweet_text || poll.title,
                                      author: poll.original_tweet?.author_name || poll.author_name,
                                      sourceType: 'twitter',
                                      sourceUrl: poll.original_tweet?.url || poll.source_url || poll.tweet_url,
                                      twitterMetadata: {
                                        authorName: poll.original_tweet?.author_name || poll.author_name,
                                        username: poll.original_tweet?.author_username || poll.author_username,
                                        profileImageUrl: poll.original_tweet?.author_profile_image || poll.profile_image_url,
                                        text: poll.original_tweet?.text || poll.original_tweet?.content || poll.tweet_text || poll.title,
                                        createdAt: poll.original_tweet?.created_at || poll.created_at,
                                        tweetUrl: poll.original_tweet?.url || poll.source_url || poll.tweet_url,
                                        metrics: poll.original_tweet?.metrics || poll.metrics
                                      }
                                    });
                                  }
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                                title="View original content"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                <span className="text-[10px] sm:text-xs">Open</span>
                              </button>
                            </div>
                            )}
                            {/* Expanded tickers - Expandable rows like Curations */}
                            {expandedWatchlists[`request_${poll.id}`] && (
                              <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                <div className={`${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50/50'}`}>
                                  {/* Original Post Display - X Display Requirements compliant */}
                                  {poll.original_tweet && (() => {
                                    // Build X profile URL for linking - Display Requirements compliance
                                    const pollUsername = poll.original_tweet?.author_username || poll.author_username;
                                    const pollXProfileUrl = pollUsername ? `https://x.com/${pollUsername}` : null;
                                    const pollSourceUrl = poll.original_tweet?.url || poll.source_url;
                                    
                                    return (
                                    <div className={`p-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                      {/* Clickable white space area linking to permalink */}
                                      <a 
                                        href={pollSourceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="block cursor-pointer"
                                      >
                                        <div className="flex items-start gap-3">
                                          {/* Profile Image - MUST link to X profile */}
                                          {(poll.original_tweet.author_profile_image || poll.profile_image_url) ? (
                                            <a href={pollXProfileUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                                              <img 
                                                src={poll.original_tweet.author_profile_image || poll.profile_image_url}
                                                alt={`${poll.original_tweet.author_name || poll.author_name} profile`}
                                                className="w-12 h-12 rounded-full cursor-pointer hover:opacity-80"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                              />
                                            </a>
                                          ) : (
                                            <a href={pollXProfileUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                                              <div className={`w-12 h-12 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 ${isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}>
                                                <svg className={`w-7 h-7 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="currentColor" viewBox="0 0 24 24">
                                                  <path d="M12 12c2.7 0 4.875-2.175 4.875-4.875S14.7 2.25 12 2.25 7.125 4.425 7.125 7.125 9.3 12 12 12zm0 2.25c-3.262 0-9.75 1.637-9.75 4.875V21h19.5v-1.875c0-3.238-6.488-4.875-9.75-4.875z"/>
                                                </svg>
                                              </div>
                                            </a>
                                          )}
                                          
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                              {/* Name and handle stacked vertically */}
                                              <div className="min-w-0">
                                                {/* Author Name - MUST link to X profile */}
                                                <span 
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    window.open(pollXProfileUrl, '_blank');
                                                  }}
                                                  className={`font-bold block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                                                >
                                                  {poll.original_tweet.author_name || poll.author_name}
                                                </span>
                                                
                                                {/* @username below name - MUST link to X profile */}
                                                <span 
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    window.open(pollXProfileUrl, '_blank');
                                                  }}
                                                  className={`block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                                                >
                                                  @{poll.original_tweet.author_username || poll.author_username}
                                                </span>
                                              </div>
                                              
                                              {/* X Logo - upper right per X Display Requirements */}
                                              <svg className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
                                              </svg>
                                            </div>
                                          </div>
                                        </div>
                                      </a>
                                      
                                      {/* Timestamp + View on X - MUST be right after author header per X Display Requirements */}
                                      <div className="flex items-center gap-2 mt-1 mb-2" style={{marginLeft: '60px'}}>
                                        {(poll.original_tweet.created_at || poll.created_at) && (
                                          <a
                                            href={pollSourceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className={`text-sm hover:underline ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                                          >
                                            {new Date(poll.original_tweet.created_at || poll.created_at).toLocaleString('en-US', {
                                              month: 'short',
                                              day: 'numeric',
                                              year: 'numeric',
                                              hour: 'numeric',
                                              minute: '2-digit',
                                              hour12: true
                                            })}
                                          </a>
                                        )}
                                        {/* View on X next to timestamp */}
                                        {pollSourceUrl && (
                                          <>
                                            {(poll.original_tweet.created_at || poll.created_at) && <span className={`${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}>·</span>}
                                            <a
                                              href={pollSourceUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => e.stopPropagation()}
                                              className="text-sm text-blue-500 hover:underline"
                                            >
                                              View on X
                                            </a>
                                          </>
                                        )}
                                      </div>
                                      
                                      {/* Tweet Content */}
                                      <div className={`text-sm leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`} style={{marginLeft: '60px'}}>
                                        {renderTextWithLinks(poll.original_tweet.text || poll.original_tweet.content || poll.title, isDarkMode)}
                                      </div>
                                      
                                      {/* Engagement Metrics */}
                                      {(poll.original_tweet.metrics || poll.metrics) && (
                                        <div className={`flex items-center gap-4 mt-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`} style={{marginLeft: '60px'}}>
                                          {((poll.original_tweet.metrics || poll.metrics)?.reply_count > 0) && (
                                            <span className="flex items-center gap-1">
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                                              </svg>
                                              {((poll.original_tweet.metrics || poll.metrics)?.reply_count || 0).toLocaleString()}
                                            </span>
                                          )}
                                          {((poll.original_tweet.metrics || poll.metrics)?.retweet_count > 0) && (
                                            <span className="flex items-center gap-1">
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                              </svg>
                                              {((poll.original_tweet.metrics || poll.metrics)?.retweet_count || 0).toLocaleString()}
                                            </span>
                                          )}
                                          {((poll.original_tweet.metrics || poll.metrics)?.like_count > 0) && (
                                            <span className="flex items-center gap-1">
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                                              </svg>
                                              {((poll.original_tweet.metrics || poll.metrics)?.like_count || 0).toLocaleString()}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );})()}
                                  
                                  {(poll.tickers_discovered || poll.tickers || []).length === 0 ? (
                                    <p className={`px-3 py-4 text-sm text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No tickers in this request</p>
                                  ) : (
                                    <div className={`p-2 ${isDarkMode ? 'bg-slate-900' : ''}`}>
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                          <thead className={`${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                                            <tr>
                                              <th className={`px-2 py-1.5 text-left font-medium sticky left-0 ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>Ticker</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>MCap</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>P/E</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV/S</th>
                                              <th className={`px-2 py-1.5 w-6`}></th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(poll.tickers_discovered || poll.tickers || []).map((ticker, idx) => {
                                              const tickerSymbol = typeof ticker === 'string' ? ticker : ticker.symbol || ticker;
                                              const tickerMetrics = allTickersData.find(d => d.ticker?.toUpperCase() === tickerSymbol?.toUpperCase()) || {};
                                              const rowKey = `req-${poll.id}-${tickerSymbol}`;
                                              const isRowExp = expandedRowTicker === rowKey;
                                              // Get replies for this ticker - check both 'tickers' and 'tickers_mentioned' fields
                                              let tickerReplies = (poll.replies || []).filter(r => {
                                                const replyTickers = r.tickers || r.tickers_mentioned || [];
                                                return replyTickers.some(t => t.toUpperCase() === tickerSymbol.toUpperCase());
                                              });
                                              // Also check ticker_details for source info and create synthetic reply if needed
                                              const tickerDetail = poll.ticker_details?.[tickerSymbol] || poll.ticker_details?.[tickerSymbol.toUpperCase()] || {};
                                              if (tickerReplies.length === 0 && tickerDetail.source_tweet) {
                                                tickerReplies = [{
                                                  text: tickerDetail.source_tweet,
                                                  author_username: (tickerDetail.source_author || '@unknown').replace('@', ''),
                                                  url: tickerDetail.source_url,
                                                  id: `detail-${tickerSymbol}`
                                                }];
                                              }
                                              
                                              return (
                                                <React.Fragment key={idx}>
                                                  <tr className={`border-t cursor-pointer ${isDarkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-200/50 hover:bg-slate-100/50'} ${isRowExp ? (isDarkMode ? 'bg-slate-700/50' : 'bg-slate-100') : ''}`}
                                                    onClick={() => {
                                                      setExpandedRowTicker(prev => prev === rowKey ? null : rowKey);
                                                      // Validate replies when expanding
                                                      if (expandedRowTicker !== rowKey) {
                                                        tickerReplies.forEach(reply => validateReply(reply));
                                                      }
                                                    }}>
                                                    <td className={`px-2 py-1.5 font-mono font-bold sticky left-0 ${isDarkMode ? 'bg-slate-800/50 text-blue-400' : 'bg-slate-50/50 text-blue-600'}`}>${tickerSymbol}</td>
                                                    <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.market_cap, 'currency')}</td>
                                                    <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.enterprise_value, 'currency')}</td>
                                                    <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.forward_pe, 'ratio')}</td>
                                                    <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.ev_to_sales, 'ratio')}</td>
                                                    <td className="px-1 py-1 text-center"><svg className={`w-4 h-4 transform transition-transform ${isRowExp ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg></td>
                                                  </tr>
                                                  {isRowExp && (
                                                    <tr><td colSpan={6} className="p-0">
                                                      <div className={`px-2 py-2 border-t ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                                        {/* Replies section - who suggested this ticker and their post content */}
                                                        {tickerReplies.length > 0 && (
                                                          <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
                                                            {tickerReplies.map((reply, replyIdx) => {
                                                              const status = validationStatus[reply.id];
                                                              const isDeleted = status?.status === 'deleted';
                                                              const fullContent = decodeHtmlEntities(reply.text || reply.content || '');
                                                              const isLong = fullContent.length > 280;
                                                              const contentKey = `reply-${reply.id || replyIdx}`;
                                                              const isContentExpanded = expandedContent[contentKey];
                                                              const displayContent = isContentExpanded ? fullContent : fullContent.substring(0, 280);
                                                              
                                                              return (
                                                                <XCompliantReplyCard
                                                                  key={replyIdx}
                                                                  reply={reply}
                                                                  replyIdx={replyIdx}
                                                                  status={status}
                                                                  isDeleted={isDeleted}
                                                                  displayContent={displayContent}
                                                                  isLong={isLong}
                                                                  isContentExpanded={isContentExpanded}
                                                                  contentKey={contentKey}
                                                                  setExpandedContent={setExpandedContent}
                                                                  isDarkMode={isDarkMode}
                                                                  renderTextWithLinks={renderTextWithLinks}
                                                                />
                                                              );
                                                            })}
                                                          </div>
                                                        )}
                                                        {/* Action buttons */}
                                                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                                          {/* Folder */}
                                                          <button 
                                                            onClick={(e) => { e.stopPropagation(); if (onSchedule) onSchedule({ ticker: tickerSymbol, bookmarkId: poll.id }); }} 
                                                            className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-purple-400 hover:bg-purple-500/10' : 'text-purple-600 hover:bg-purple-50'}`}
                                                          >
                                                            <FolderIcon className="w-5 h-5" />
                                                            <span className="text-[10px]">Folder</span>
                                                          </button>
                                                          {/* File */}
                                                          <button 
                                                            onClick={(e) => { e.stopPropagation(); if (onDocuments) onDocuments({ ticker: tickerSymbol, bookmarkId: poll.id }); }} 
                                                            className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-slate-400 hover:bg-slate-500/10' : 'text-slate-600 hover:bg-slate-100'}`}
                                                          >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                                            <span className="text-[10px]">File</span>
                                                          </button>
                                                          {/* Ticker */}
                                                          <button 
                                                            onClick={(e) => { e.stopPropagation(); if (onWatchlist) onWatchlist(tickerSymbol); }} 
                                                            className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-green-400 hover:bg-green-500/10' : 'text-green-600 hover:bg-green-50'}`}
                                                          >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                                            <span className="text-[10px]">Ticker</span>
                                                          </button>
                                                          {/* Share */}
                                                          <button 
                                                            onClick={(e) => { e.stopPropagation(); if (onShare) onShare({ ticker: tickerSymbol, bookmarkId: poll.id, content: poll.title }); }} 
                                                            className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-yellow-400 hover:bg-yellow-500/10' : 'text-yellow-600 hover:bg-yellow-50'}`}
                                                          >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/></svg>
                                                            <span className="text-[10px]">Share</span>
                                                          </button>
                                                        </div>
                                                      </div>
                                                    </td></tr>
                                                  )}
                                                </React.Fragment>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  )}
                </div>
              )}
              
              {/* X Polls Section - Actual Twitter polls with voting options */}
              {(watchlistTypeFilter === 'all' || watchlistTypeFilter === 'polls') && (
                <div className={`rounded-lg border ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                  <button 
                    onClick={() => toggleCategory('polls')}
                    className={`w-full px-4 py-4 text-sm font-bold uppercase tracking-wide flex items-center justify-between gap-3 ${isDarkMode ? 'text-slate-200 hover:bg-slate-700/50' : 'text-slate-700 hover:bg-slate-50'} rounded-t-lg transition-colors`}
                  >
                    <div className="flex items-center gap-3">
                      <svg className={`w-6 h-6 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      X POLLS ({polls.filter(p => {
                        if (!p.is_own_poll) return false;
                        // X Polls: source is 'x_poll' OR has poll options pattern in content
                        const isXPoll = p.source === 'x_poll' || 
                          (/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || ''));
                        return isXPoll;
                      }).length})
                    </div>
                    <svg className={`w-6 h-6 transition-transform ${expandedCategories.has('polls') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                  {expandedCategories.has('polls') && (
                  <div className={`space-y-2 p-2 border-t ${isDarkMode ? 'border-slate-700 bg-white' : 'border-slate-200'}`}>
                    {polls.filter(p => {
                      if (!p.is_own_poll) return false;
                      const isXPoll = p.source === 'x_poll' || 
                        (/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || ''));
                      return isXPoll;
                    }).length === 0 ? (
                      <div className={`p-4 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        No X polls yet. Create one and bookmark it on X to import!
                      </div>
                    ) : (
                    polls
                      .filter(p => {
                        if (!p.is_own_poll) return false;
                        // X Polls: source is 'x_poll' OR has poll options pattern in content
                        const isXPoll = p.source === 'x_poll' || 
                          (/\b[abcd]\)|[1-4][\.\)]\s*\$?[a-zA-Z]/i.test(p.original_tweet?.content || p.title || ''));
                        return isXPoll;
                      })
                      .sort((a, b) => new Date(b.created_at || b.updated_at || 0) - new Date(a.created_at || a.updated_at || 0))
                      .map(poll => {
                        const daysOld = calculateDaysOld(poll.created_at || poll.updated_at);
                        const ageCategory = daysOld < 10 ? 'New' : daysOld <= 30 ? 'Recent' : 'Older';
                        const ageBorderColor = ageCategory === 'New' ? 'border-green-400' : ageCategory === 'Recent' ? 'border-orange-400' : 'border-red-400';
                        const ageBgColor = ageCategory === 'New' ? 'bg-green-100 text-green-800' : ageCategory === 'Recent' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                        // Status bar color for X Polls section
                        const statusBarColor = ageCategory === 'New' ? 'bg-emerald-500' : ageCategory === 'Recent' ? 'bg-amber-500' : 'bg-rose-500';
                        
                        return (
                          <div key={poll.id} className={`relative rounded-lg overflow-hidden border transition-all duration-200 hover:-translate-y-[1px] ${isDarkMode ? 'bg-[#1a2235] border-white/5 hover:border-white/10 hover:shadow-card-hover' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'}`}>
                            {/* Status Bar - Left Edge */}
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusBarColor}`} />
                            {/* Card content - clickable to expand */}
                            <button
                              onClick={() => toggleWatchlistExpansion(`poll_${poll.id}`, { tickers: poll.tickers_discovered || poll.tickers || poll.all_tickers || [] })}
                              className={`w-full px-4 py-3 ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors text-left`}
                            >
                              <div className="space-y-0.5">
                                {/* Row 1: X Logo + Title | stats (far right) */}
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex items-center gap-1.5">
                                    {/* X Logo - to the left of title */}
                                    {(poll.original_tweet?.url?.includes('x.com') || poll.original_tweet?.url?.includes('twitter.com') || poll.source_url?.includes('x.com') || poll.source_url?.includes('twitter.com') || poll.tweet_id) && (
                                      <svg className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                      </svg>
                                    )}
                                    <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{poll.title}</div>
                                  </div>
                                  <span className={`text-xs flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    {poll.ticker_count || 0} tickers · {poll.reply_count || 0} replies
                                  </span>
                                </div>
                                
                                {/* Row 2: Profile pic + @author | Arrow (far right) */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 ml-6">
                                    {(poll.original_tweet?.author_profile_image || poll.profile_image_url || poll.author_profile_image) && (
                                      <img src={poll.original_tweet?.author_profile_image || poll.profile_image_url || poll.author_profile_image} alt="" className="w-5 h-5 rounded-full flex-shrink-0 object-cover" />
                                    )}
                                    {(poll.original_tweet?.author_username || poll.author_username || poll.author_name) && (
                                      <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        @{(poll.original_tweet?.author_username || poll.author_username || poll.author_name || '').replace('@', '')}
                                      </span>
                                    )}
                                  </div>
                                  <svg className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${expandedWatchlists[`poll_${poll.id}`] ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                  </svg>
                                </div>
                              </div>
                            </button>
                            {/* Action buttons - Polls - Only show when expanded */}
                            {expandedWatchlists[`poll_${poll.id}`] && (
                            <div className={`flex flex-wrap items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 justify-center border-t backdrop-blur-sm ${isDarkMode ? 'border-slate-700 bg-slate-900/60' : 'border-slate-200 bg-slate-50/80'}`}>
                              {/* Refresh button */}
                              <button
                                data-poll-id={poll.id}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const btn = e.currentTarget;
                                  const pollId = btn.getAttribute('data-poll-id');
                                  if (!pollId) {
                                    showNotification?.('Unable to refresh - poll ID not found', 'error');
                                    return;
                                  }
                                  try {
                                    btn.disabled = true;
                                    btn.querySelector('svg').classList.add('animate-spin');
                                    const response = await fetch(`${API_BASE_URL}/api/fishing-threads/${pollId}/fetch-replies`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ user_id: user?.id || user?.user_id || user?.email })
                                    });
                                    const result = await response.json();
                                    if (result.success) {
                                      const newCount = result.new_tickers || 0;
                                      const existingCount = result.already_known || 0;
                                      const totalReplies = result.replies_found || 0;
                                      let msg = `Refresh complete: ${totalReplies} replies`;
                                      if (newCount > 0) msg += `, ${newCount} new tickers`;
                                      if (existingCount > 0) msg += `, ${existingCount} already in list`;
                                      showNotification?.(msg, 'success');
                                      // Update the poll data locally with all details
                                      const updates = {
                                        reply_count: totalReplies,
                                        ticker_count: result.tickers_found?.length || 0,
                                        tickers_discovered: result.tickers_found || [],
                                        ticker_details: result.ticker_details || {}
                                      };
                                      onPollUpdated?.(pollId, updates);
                                      // Also update selectedCollectionPoll if viewing this poll
                                      if (selectedCollectionPoll?.id === pollId) {
                                        setSelectedCollectionPoll(prev => ({ ...prev, ...updates }));
                                      }
                                    } else {
                                      showNotification?.(result.error || 'Failed to refresh replies', 'error');
                                    }
                                  } catch (err) {
                                    console.error('Error refreshing poll:', err);
                                    showNotification?.('Failed to fetch thread replies. Please try again.', 'error');
                                  } finally {
                                    btn.disabled = false;
                                    btn.querySelector('svg')?.classList.remove('animate-spin');
                                  }
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-300 text-green-600 hover:bg-green-50'}`}
                                title="Refresh poll replies"
                                data-testid={`poll-refresh-btn-${poll.id}`}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Refresh</span>
                              </button>
                              {/* Delete button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeletePoll?.(poll.id, poll.title);
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                                title="Delete poll"
                                data-testid={`poll-delete-btn-${poll.id}`}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Delete</span>
                              </button>
                              {/* Share button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onShare?.({
                                    ticker: poll.title,
                                    bookmarkId: poll.id,
                                    content: `Poll: ${poll.title}`,
                                    shareType: 'poll',
                                    tickerCount: poll.ticker_count || 0,
                                    pollId: poll.id
                                  });
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}
                                title="Share poll"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Share</span>
                              </button>
                              {/* Open button - Opens original content in modal */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Open original content in modal - like Curations (X Polls)
                                  if (onOriginal) {
                                    onOriginal({
                                      isIdeasList: true,
                                      isTwitterContent: true,
                                      platformSource: 'Twitter',
                                      bookmarkId: poll.tweet_id || poll.id,
                                      ticker: poll.title,
                                      content: poll.original_tweet?.text || poll.original_tweet?.content || poll.tweet_text || poll.title,
                                      author: poll.original_tweet?.author_name || poll.author_name,
                                      sourceType: 'twitter',
                                      sourceUrl: poll.original_tweet?.url || poll.source_url || poll.tweet_url,
                                      twitterMetadata: {
                                        authorName: poll.original_tweet?.author_name || poll.author_name,
                                        username: poll.original_tweet?.author_username || poll.author_username,
                                        profileImageUrl: poll.original_tweet?.author_profile_image || poll.profile_image_url,
                                        text: poll.original_tweet?.text || poll.original_tweet?.content || poll.tweet_text || poll.title,
                                        createdAt: poll.original_tweet?.created_at || poll.created_at,
                                        tweetUrl: poll.original_tweet?.url || poll.source_url || poll.tweet_url,
                                        metrics: poll.original_tweet?.metrics || poll.metrics
                                      }
                                    });
                                  }
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                                title="View original content"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                <span className="text-[10px] sm:text-xs">Open</span>
                              </button>
                            </div>
                            )}
                            {/* Expanded tickers - Expandable rows like Curations */}
                            {expandedWatchlists[`poll_${poll.id}`] && (
                              <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                <div className={`${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50/50'}`}>
                                  {/* Original Post Display - X Display Requirements compliant */}
                                  {poll.original_tweet && (
                                    <div className={`p-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                      {(() => {
                                        const pollUsername = poll.original_tweet.author_username || poll.author_username;
                                        const pollXProfileUrl = pollUsername ? `https://x.com/${pollUsername.replace('@', '')}` : null;
                                        const pollSourceUrl = poll.original_tweet.url || poll.source_url;
                                        
                                        return (
                                          <>
                                          {/* Clickable white space area linking to permalink */}
                                          <a 
                                            href={pollSourceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="block cursor-pointer"
                                          >
                                            <div className="flex items-start gap-3">
                                              {/* Profile Image - MUST link to X profile */}
                                              {(poll.original_tweet.author_profile_image || poll.profile_image_url) ? (
                                                <span 
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    window.open(pollXProfileUrl, '_blank');
                                                  }}
                                                  className="flex-shrink-0"
                                                >
                                                  <img 
                                                    src={poll.original_tweet.author_profile_image || poll.profile_image_url}
                                                    alt={`${poll.original_tweet.author_name || poll.author_name} profile`}
                                                    className="w-12 h-12 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                  />
                                                </span>
                                              ) : (
                                                <span 
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    window.open(pollXProfileUrl, '_blank');
                                                  }}
                                                  className="flex-shrink-0"
                                                >
                                                  <div className={`w-12 h-12 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity ${isDarkMode ? 'bg-slate-600' : 'bg-slate-300'}`}>
                                                    <svg className={`w-7 h-7 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="currentColor" viewBox="0 0 24 24">
                                                      <path d="M12 12c2.7 0 4.875-2.175 4.875-4.875S14.7 2.25 12 2.25 7.125 4.425 7.125 7.125 9.3 12 12 12zm0 2.25c-3.262 0-9.75 1.637-9.75 4.875V21h19.5v-1.875c0-3.238-6.488-4.875-9.75-4.875z"/>
                                                    </svg>
                                                  </div>
                                                </span>
                                              )}
                                        
                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                  {/* Name and handle stacked vertically */}
                                                  <div className="min-w-0">
                                                    {/* Author Name - MUST link to X profile */}
                                                    <span 
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        window.open(pollXProfileUrl, '_blank');
                                                      }}
                                                      className={`font-bold block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                                                    >
                                                      {poll.original_tweet.author_name || poll.author_name}
                                                    </span>
                                                    
                                                    {/* @username below name - MUST link to X profile */}
                                                    <span 
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        window.open(pollXProfileUrl, '_blank');
                                                      }}
                                                      className={`block truncate hover:underline cursor-pointer ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                                                    >
                                                      @{poll.original_tweet.author_username || poll.author_username}
                                                    </span>
                                                  </div>
                                                  
                                                  {/* X Logo - upper right per X Display Requirements */}
                                                  <svg className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M14.258 10.152L23.176 0h-2.113l-7.747 8.813L7.133 0H0l9.352 13.328L0 23.973h2.113l8.176-9.309 6.531 9.309h7.133zm-2.895 3.293l-.949-1.328L2.875 1.56h3.246l6.086 8.523.945 1.328 7.91 11.078h-3.246z"/>
                                                  </svg>
                                                </div>
                                              </div>
                                            </div>
                                          </a>
                                          
                                          {/* Timestamp + View on X - below author header */}
                                          <div className="flex items-center gap-2 mb-2" style={{marginLeft: '60px'}}>
                                            {(poll.original_tweet.created_at || poll.created_at) && (
                                              <a
                                                href={pollSourceUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className={`text-sm hover:underline ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
                                              >
                                                {new Date(poll.original_tweet.created_at || poll.created_at).toLocaleString('en-US', {
                                                  month: 'short',
                                                  day: 'numeric',
                                                  year: 'numeric',
                                                  hour: 'numeric',
                                                  minute: '2-digit',
                                                  hour12: true
                                                })}
                                              </a>
                                            )}
                                            {/* View on X next to timestamp */}
                                            {pollSourceUrl && (
                                              <>
                                                {(poll.original_tweet.created_at || poll.created_at) && <span className={`${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}>·</span>}
                                                <a
                                                  href={pollSourceUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  onClick={(e) => e.stopPropagation()}
                                                  className="text-sm text-blue-500 hover:underline"
                                                >
                                                  View on X
                                                </a>
                                              </>
                                            )}
                                          </div>
                                          
                                          {/* Tweet Content */}
                                          <div className={`text-sm leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`} style={{marginLeft: '60px'}}>
                                            {renderTextWithLinks(poll.original_tweet.text || poll.original_tweet.content || poll.title, isDarkMode)}
                                          </div>
                                          
                                          {/* Engagement Metrics */}
                                          {(poll.original_tweet.metrics || poll.metrics) && (
                                            <div className={`flex items-center gap-4 mt-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`} style={{marginLeft: '60px'}}>
                                              {((poll.original_tweet.metrics || poll.metrics)?.reply_count > 0) && (
                                                <span className="flex items-center gap-1">
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                                                  </svg>
                                                  {((poll.original_tweet.metrics || poll.metrics)?.reply_count || 0).toLocaleString()}
                                                </span>
                                              )}
                                              {((poll.original_tweet.metrics || poll.metrics)?.retweet_count > 0) && (
                                                <span className="flex items-center gap-1">
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                                  </svg>
                                                  {((poll.original_tweet.metrics || poll.metrics)?.retweet_count || 0).toLocaleString()}
                                                </span>
                                              )}
                                              {((poll.original_tweet.metrics || poll.metrics)?.like_count > 0) && (
                                                <span className="flex items-center gap-1">
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                                                  </svg>
                                                  {((poll.original_tweet.metrics || poll.metrics)?.like_count || 0).toLocaleString()}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}
                                  
                                  {(poll.tickers_discovered || poll.tickers || []).length === 0 ? (
                                    <p className={`px-3 py-4 text-sm text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No tickers in this poll</p>
                                  ) : (
                                    <div className={`p-2 ${isDarkMode ? 'bg-slate-900' : ''}`}>
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                          <thead className={`${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                                            <tr>
                                              <th className={`px-2 py-1.5 text-left font-medium sticky left-0 ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>Ticker</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>MCap</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>P/E</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV/S</th>
                                              <th className={`px-2 py-1.5 w-6`}></th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(poll.tickers_discovered || poll.tickers || []).map((ticker, idx) => {
                                              const tickerSymbol = typeof ticker === 'string' ? ticker : ticker.symbol || ticker;
                                              const tickerMetrics = allTickersData.find(d => d.ticker?.toUpperCase() === tickerSymbol?.toUpperCase()) || {};
                                              const rowKey = `poll-${poll.id}-${tickerSymbol}`;
                                              const isRowExp = expandedRowTicker === rowKey;
                                              // Get replies for this ticker - check both 'tickers' and 'tickers_mentioned' fields
                                              let tickerReplies = (poll.replies || []).filter(r => {
                                                const replyTickers = r.tickers || r.tickers_mentioned || [];
                                                return replyTickers.some(t => t.toUpperCase() === tickerSymbol.toUpperCase());
                                              });
                                              // Also check ticker_details for source info and create synthetic reply if needed
                                              const tickerDetail = poll.ticker_details?.[tickerSymbol] || poll.ticker_details?.[tickerSymbol.toUpperCase()] || {};
                                              if (tickerReplies.length === 0 && tickerDetail.source_tweet) {
                                                tickerReplies = [{
                                                  text: tickerDetail.source_tweet,
                                                  author_username: (tickerDetail.source_author || '@unknown').replace('@', ''),
                                                  url: tickerDetail.source_url,
                                                  id: `detail-${tickerSymbol}`
                                                }];
                                              }
                                              
                                              return (
                                                <React.Fragment key={idx}>
                                                  <tr className={`border-t cursor-pointer ${isDarkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-200/50 hover:bg-slate-100/50'} ${isRowExp ? (isDarkMode ? 'bg-slate-700/50' : 'bg-slate-100') : ''}`}
                                                    onClick={() => {
                                                      setExpandedRowTicker(prev => prev === rowKey ? null : rowKey);
                                                      // Validate replies when expanding
                                                      if (expandedRowTicker !== rowKey) {
                                                        tickerReplies.forEach(reply => validateReply(reply));
                                                      }
                                                    }}>
                                                    <td className={`px-2 py-1.5 font-mono font-bold sticky left-0 ${isDarkMode ? 'bg-slate-800/50 text-blue-400' : 'bg-slate-50/50 text-blue-600'}`}>${tickerSymbol}</td>
                                                    <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.market_cap, 'currency')}</td>
                                                    <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.enterprise_value, 'currency')}</td>
                                                    <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.forward_pe, 'ratio')}</td>
                                                    <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.ev_to_sales, 'ratio')}</td>
                                                    <td className="px-1 py-1 text-center"><svg className={`w-4 h-4 transform transition-transform ${isRowExp ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg></td>
                                                  </tr>
                                                  {isRowExp && (
                                                    <tr><td colSpan={6} className="p-0">
                                                      <div className={`px-2 py-2 border-t ${isDarkMode ? 'bg-[#141c2e] border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                                        {/* Replies section - who suggested this ticker and their post content */}
                                                        {tickerReplies.length > 0 && (
                                                          <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
                                                            {tickerReplies.map((reply, replyIdx) => {
                                                              const status = validationStatus[reply.id];
                                                              const isDeleted = status?.status === 'deleted';
                                                              const fullContent = decodeHtmlEntities(reply.text || reply.content || '');
                                                              const isLong = fullContent.length > 280;
                                                              const contentKey = `reply-${reply.id || replyIdx}`;
                                                              const isContentExpanded = expandedContent[contentKey];
                                                              const displayContent = isContentExpanded ? fullContent : fullContent.substring(0, 280);
                                                              
                                                              return (
                                                                <XCompliantReplyCard
                                                                  key={replyIdx}
                                                                  reply={reply}
                                                                  replyIdx={replyIdx}
                                                                  status={status}
                                                                  isDeleted={isDeleted}
                                                                  displayContent={displayContent}
                                                                  isLong={isLong}
                                                                  isContentExpanded={isContentExpanded}
                                                                  contentKey={contentKey}
                                                                  setExpandedContent={setExpandedContent}
                                                                  isDarkMode={isDarkMode}
                                                                  renderTextWithLinks={renderTextWithLinks}
                                                                />
                                                              );
                                                            })}
                                                          </div>
                                                        )}
                                                        {/* Action buttons */}
                                                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                                          {/* Folder */}
                                                          <button 
                                                            onClick={(e) => { e.stopPropagation(); if (onSchedule) onSchedule({ ticker: tickerSymbol, bookmarkId: poll.id }); }} 
                                                            className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-purple-400 hover:bg-purple-500/10' : 'text-purple-600 hover:bg-purple-50'}`}
                                                          >
                                                            <FolderIcon className="w-5 h-5" />
                                                            <span className="text-[10px]">Folder</span>
                                                          </button>
                                                          {/* File */}
                                                          <button 
                                                            onClick={(e) => { e.stopPropagation(); if (onDocuments) onDocuments({ ticker: tickerSymbol, bookmarkId: poll.id }); }} 
                                                            className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-slate-400 hover:bg-slate-500/10' : 'text-slate-600 hover:bg-slate-100'}`}
                                                          >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                                            <span className="text-[10px]">File</span>
                                                          </button>
                                                          {/* Ticker */}
                                                          <button 
                                                            onClick={(e) => { e.stopPropagation(); if (onWatchlist) onWatchlist(tickerSymbol); }} 
                                                            className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-green-400 hover:bg-green-500/10' : 'text-green-600 hover:bg-green-50'}`}
                                                          >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                                            <span className="text-[10px]">Ticker</span>
                                                          </button>
                                                          {/* Share */}
                                                          <button 
                                                            onClick={(e) => { e.stopPropagation(); if (onShare) onShare({ ticker: tickerSymbol, bookmarkId: poll.id, content: poll.title }); }} 
                                                            className={`flex flex-col items-center justify-center flex-1 min-w-[50px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-yellow-400 hover:bg-yellow-500/10' : 'text-yellow-600 hover:bg-yellow-50'}`}
                                                          >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/></svg>
                                                            <span className="text-[10px]">Share</span>
                                                          </button>
                                                        </div>
                                                      </div>
                                                    </td></tr>
                                                  )}
                                                </React.Fragment>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  )}
                </div>
              )}
              
              {/* Empty state when no watchlists exist (in My Watchlists view) */}
              {watchlists.length === 0 && (
                <div className={`p-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                  </svg>
                  <p className="text-lg font-medium mb-2">No watchlists yet</p>
                  <p className="text-sm">Create your first watchlist to start tracking tickers and metrics!</p>
                </div>
              )}
                </>
              ) : (
                <>
                  {/* Received/Shared Watchlists Section - Only shows in "Received" tab */}
                  {sharedWatchlists.length > 0 ? (
                    <div className="space-y-2">
                      {sharedWatchlists.map(wl => (
                        <div key={wl.id} className={`rounded-lg border ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'} overflow-hidden`}>
                          {/* Row 1: Name + down arrow */}
                          <button
                            onClick={() => toggleWatchlistExpansion(`shared_${wl.id}`, wl)}
                            className={`w-full px-4 py-3 flex items-start justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors text-left`}
                          >
                            <div className={`font-medium flex-1 min-w-0 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{wl.name}</div>
                            <svg className={`w-5 h-5 flex-shrink-0 mt-1 transition-transform duration-200 ${expandedWatchlists[`shared_${wl.id}`] ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                            </svg>
                          </button>
                          {/* Row 2: Shared info */}
                          <div className={`px-4 py-2`}>
                            <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Shared by {wl.sender_name || wl.sender_email} · {wl.ticker_count || 0} tickers
                            </div>
                          </div>
                            {/* Row 3: Action buttons - Only show when expanded */}
                            {expandedWatchlists[`shared_${wl.id}`] && (
                            <div className={`flex flex-wrap items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 justify-center border-t backdrop-blur-sm ${isDarkMode ? 'border-slate-700 bg-slate-900/60' : 'border-slate-200 bg-slate-50/80'}`}>
                              {/* Delete button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onDeleteSharedItem) {
                                    onDeleteSharedItem(wl.id, wl.name);
                                  }
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                                title="Remove from shared"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Delete</span>
                              </button>
                              {/* Share button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onShare?.({ 
                                    ticker: wl.name, 
                                    watchlistId: wl.watchlist_id || wl.id,
                                    content: `Watchlist: ${wl.name}`,
                                    shareType: 'watchlist',
                                    tickerCount: wl.ticker_count || 0
                                  });
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}`}
                                title="Share watchlist"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                                </svg>
                                <span className="text-[10px] sm:text-xs">Share</span>
                              </button>
                              {/* Open button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (wl.watchlist_id) {
                                    const actualWatchlist = watchlists.find(w => w.id === wl.watchlist_id);
                                    if (actualWatchlist) {
                                      setViewAllMode(false);
                                      setSelectedWatchlist(actualWatchlist);
                                    }
                                  }
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                                title="Open curation"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span className="text-[10px] sm:text-xs">Open</span>
                              </button>
                            </div>
                            )}
                            {/* Expanded tickers spreadsheet */}
                            {expandedWatchlists[`shared_${wl.id}`] && (
                              <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                <div className={`${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50/50'}`}>
                                  {(wl.tickers || []).length === 0 ? (
                                    <p className={`px-3 py-4 text-sm text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No tickers in this watchlist</p>
                                  ) : (
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead className={`${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                                          <tr>
                                            <th className={`px-2 py-1.5 text-left font-medium sticky left-0 ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>Ticker</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Mkt Cap</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Fwd P/E</th>
                                            <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV/Sales</th>
                                            <th className={`px-2 py-1.5 text-center font-medium hidden sm:table-cell ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>P/B</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(wl.tickers || []).map((ticker, idx) => {
                                            const tickerSymbol = typeof ticker === 'string' ? ticker : ticker.symbol || ticker;
                                            const tickerMetrics = allTickersData.find(d => d.ticker?.toUpperCase() === tickerSymbol?.toUpperCase()) || {};
                                            return (
                                              <tr key={`shared-${wl.id || wl.name}-${tickerSymbol}-${idx}`} className={`border-t ${isDarkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-200/50 hover:bg-slate-100/50'}`}>
                                                <td className={`px-2 py-1.5 font-mono font-bold sticky left-0 ${isDarkMode ? 'bg-slate-800/50 text-blue-400' : 'bg-slate-50/50 text-blue-600'}`}>${tickerSymbol}</td>
                                                <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.market_cap, 'currency')}</td>
                                                <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.enterprise_value, 'currency')}</td>
                                                <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.forward_pe, 'ratio')}</td>
                                                <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.ev_to_sales, 'ratio')}</td>
                                                <td className={`px-2 py-1.5 text-center font-mono hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.price_to_book, 'ratio')}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  ) : null}
                  
                  {/* Received Polls Section */}
                  {sharedPolls.length > 0 && (
                    <div className="mt-4">
                      <div className={`px-3 py-2 text-sm font-bold uppercase tracking-wide flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        <svg className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                        </svg>
                        Received Polls ({sharedPolls.length})
                      </div>
                      <div className="space-y-2">
                        {sharedPolls.map(poll => {
                          const daysOld = calculateDaysOld(poll.created_at || poll.shared_at);
                          const ageCategory = daysOld < 10 ? 'New' : daysOld <= 30 ? 'Recent' : 'Older';
                          const ageBorderColor = ageCategory === 'New' ? 'border-green-400' : ageCategory === 'Recent' ? 'border-orange-400' : 'border-red-400';
                          const ageBgColor = ageCategory === 'New' ? 'bg-green-100 text-green-800' : ageCategory === 'Recent' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                          // Status bar color for Shared Polls section
                          const statusBarColor = ageCategory === 'New' ? 'bg-emerald-500' : ageCategory === 'Recent' ? 'bg-amber-500' : 'bg-rose-500';
                          
                          return (
                            <div key={poll.id} className={`relative rounded-lg overflow-hidden border transition-all duration-200 hover:-translate-y-[1px] ${isDarkMode ? 'bg-[#1a2235] border-white/5 hover:border-white/10 hover:shadow-card-hover' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'}`}>
                              {/* Status Bar - Left Edge */}
                              <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusBarColor}`} />
                              {/* Row 1: Poll title + down arrow */}
                              <button
                                onClick={() => toggleWatchlistExpansion(`sharedpoll_${poll.id}`, { tickers: poll.tickers_discovered || poll.tickers || poll.all_tickers || [] })}
                                className={`w-full px-4 py-3 flex items-start justify-between ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'} transition-colors text-left`}
                              >
                                <div className={`font-medium flex-1 min-w-0 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{poll.title || poll.name}</div>
                                <svg className={`w-5 h-5 flex-shrink-0 mt-1 transition-transform duration-200 ${expandedWatchlists[`sharedpoll_${poll.id}`] ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                                </svg>
                              </button>
                              {/* Row 2: Shared info + age badge */}
                              <div className={`px-4 py-2 flex items-center justify-between`}>
                                <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  Shared by {poll.sender_name || poll.sender_email} · {poll.ticker_count || 0} tickers
                                </div>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ageBgColor}`}>
                                  {ageCategory}
                                </span>
                              </div>
                              {/* Row 3: Action buttons - Only show when expanded */}
                              {expandedWatchlists[`sharedpoll_${poll.id}`] && (
                              <div className={`flex flex-wrap items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 justify-center border-t backdrop-blur-sm ${isDarkMode ? 'border-slate-700 bg-slate-900/60' : 'border-slate-200 bg-slate-50/80'}`}>
                                <button
                                  onClick={() => setSelectedCollectionPoll(poll)}
                                  className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                                  title="Open poll"
                                >
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  <span className="text-[10px] sm:text-xs">Open</span>
                                </button>
                              </div>
                              )}
                              {/* Expanded tickers spreadsheet */}
                              {expandedWatchlists[`sharedpoll_${poll.id}`] && (
                                <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                  <div className={`${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50/50'}`}>
                                    {(poll.tickers_discovered || poll.tickers || []).length === 0 ? (
                                      <p className={`px-3 py-4 text-sm text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No tickers in this poll</p>
                                    ) : (
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                          <thead className={`${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                                            <tr>
                                              <th className={`px-2 py-1.5 text-left font-medium sticky left-0 ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>Ticker</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Mkt Cap</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Fwd P/E</th>
                                              <th className={`px-2 py-1.5 text-center font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>EV/Sales</th>
                                              <th className={`px-2 py-1.5 text-center font-medium hidden sm:table-cell ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>P/B</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(poll.tickers_discovered || poll.tickers || []).map((ticker, idx) => {
                                              const tickerSymbol = typeof ticker === 'string' ? ticker : ticker.symbol || ticker;
                                              const tickerMetrics = allTickersData.find(d => d.ticker?.toUpperCase() === tickerSymbol?.toUpperCase()) || {};
                                              return (
                                                <tr key={`poll-${poll.id || poll.title}-${tickerSymbol}-${idx}`} className={`border-t ${isDarkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-slate-200/50 hover:bg-slate-100/50'}`}>
                                                  <td className={`px-2 py-1.5 font-mono font-bold sticky left-0 ${isDarkMode ? 'bg-slate-800/50 text-blue-400' : 'bg-slate-50/50 text-blue-600'}`}>${tickerSymbol}</td>
                                                  <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.market_cap, 'currency')}</td>
                                                  <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.enterprise_value, 'currency')}</td>
                                                  <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.forward_pe, 'ratio')}</td>
                                                  <td className={`px-2 py-1.5 text-center font-mono ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.ev_to_sales, 'ratio')}</td>
                                                  <td className={`px-2 py-1.5 text-center font-mono hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{formatNumber(tickerMetrics.price_to_book, 'ratio')}</td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Empty state when no received content */}
                  {sharedWatchlists.length === 0 && sharedPolls.length === 0 && (
                    <div className={`p-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
                      </svg>
                      <p className="text-lg font-medium mb-2">No received ticker lists</p>
                      <p className="text-sm">Ticker lists shared with you will appear here.</p>
                    </div>
                  )}
                </>
              )}
              
              {/* Empty state when no lists exist AND no shared lists (only shows if both tabs are empty) */}
              {watchlists.length === 0 && sharedWatchlists.length === 0 && watchlistView === 'my' && (
                <div className={`p-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                  </svg>
                  <p className="text-lg font-medium mb-2">No lists yet</p>
                  <p className="text-sm">Create your first list to start tracking tickers and metrics!</p>
                </div>
              )}
            </div>
          )}

          {/* Empty state when no list selected (and not in View All mode) - only shows if user deselects */}
          {!selectedWatchlist && !viewAllMode && watchlists.length > 0 && (
            <div className={`p-3 sm:p-4 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              <p className="text-lg font-medium mb-2">Select a list to view tickers</p>
              <p className="text-sm">Choose a list from the dropdown above to see your tracked tickers and metrics, or create a new list.</p>
            </div>
          )}

          {/* Source Info Display for Ideas List Collections - styled like the card header */}
          {selectedWatchlist && (selectedWatchlist.source_info || selectedWatchlist.description) && (
            <div className={`mb-4 p-4 rounded-lg border ${
              selectedWatchlist.source_info 
                ? (isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gradient-to-r from-blue-50 to-slate-50 border-blue-200')
                : (isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200')
            }`}>
              {selectedWatchlist.source_info ? (
                <div className="space-y-3">
                  {/* Author row - like card header */}
                  <div className="flex items-center gap-3">
                    {selectedWatchlist.source_info.profile_image && (
                      <img 
                        src={selectedWatchlist.source_info.profile_image} 
                        alt="Author" 
                        className={`w-10 h-10 rounded-full border-2 ${isDarkMode ? 'border-slate-600' : 'border-blue-200'}`}
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {selectedWatchlist.source_info.author_name && (
                          <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                            {selectedWatchlist.source_info.author_name}
                          </span>
                        )}
                        {selectedWatchlist.source_info.author && (
                          <span className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                            @{selectedWatchlist.source_info.author}
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          Ideas List
                        </span>
                      </div>
                      {/* Timestamp */}
                      {selectedWatchlist.source_info.source_date && (
                        <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {new Date(selectedWatchlist.source_info.source_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Description/Summary */}
                  {selectedWatchlist.description && (
                    <div className={`text-sm p-2 rounded border ${isDarkMode ? 'text-slate-300 bg-slate-700/50 border-slate-600' : 'text-slate-700 bg-white/50 border-slate-200'}`}>
                      {selectedWatchlist.description}
                    </div>
                  )}
                  
                  {/* Ticker count and Original link */}
                  <div className={`flex items-center justify-between text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span>{selectedWatchlist.source_info.ticker_count || selectedWatchlist.tickers?.length || 0} tickers from this Ideas List</span>
                    {selectedWatchlist.source_info.source_url && (
                      <button
                        onClick={() => onOriginal?.({
                          ticker: selectedWatchlist.name,
                          watchlistId: selectedWatchlist.id,
                          bookmarkId: selectedWatchlist.source_info?.bookmark_id,
                          sourceUrl: selectedWatchlist.source_info?.source_url,
                          content: selectedWatchlist.description || selectedWatchlist.source_info?.original_summary,
                          author: selectedWatchlist.source_info?.author_name || selectedWatchlist.source_info?.author,
                          sourceType: selectedWatchlist.source_info?.source_type,
                          isIdeasList: true
                        })}
                        className={`flex items-center gap-1 px-2 py-1 rounded font-medium transition-colors ${
                          isDarkMode
                            ? 'text-blue-400 hover:bg-blue-900/50'
                            : 'text-blue-600 hover:bg-blue-50'
                        }`}
                        title="View original post"
                        data-testid="ideas-list-detail-original-btn"
                      >
                        <ExternalLink className="h-3 w-3" />
                        View Original
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    {selectedWatchlist.name}
                  </div>
                  {selectedWatchlist.description && (
                    <div className={`text-xs italic ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      "{selectedWatchlist.description}"
                    </div>
                  )}
                </>
              )}
            </div>
          )}

      {/* Categorized View OR Spreadsheet Table - only show when watchlist selected */}
      {selectedWatchlist && (
        selectedWatchlist?.source_info?.categorized_tickers?.length > 0 ? (
        // Render categorized view like Ideas List expanded - TABLE FORMAT
        <div className={`rounded-lg border shadow-sm overflow-hidden ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
          {selectedWatchlist.source_info.categorized_tickers.map((category, catIdx) => (
            <div key={catIdx}>
              {/* Category header */}
              <div className={`flex items-center gap-2 text-sm font-semibold px-4 py-3 border-b ${isDarkMode ? 'text-slate-200 bg-slate-800 border-slate-700' : 'text-slate-700 bg-slate-100 border-slate-200'}`}>
                <span className="text-lg">{category.emoji}</span>
                <span>{category.category}</span>
                <span className={`text-xs font-normal ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  ({category.tickers?.length || 0})
                </span>
              </div>
              
              {/* Table wrapper */}
              <div className="overflow-x-auto relative">
              <table className="w-full text-sm">
                {/* Column headers - mobile optimized with sort functionality */}
                <thead className={`text-xs uppercase tracking-wide ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
                  <tr>
                    <th 
                      className={`px-3 py-2 text-left sticky left-0 cursor-pointer hover:text-orange-500 ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}
                      onClick={() => handleSort('ticker')}
                    >
                      <div className="flex items-center">
                        Ticker
                        {renderSortIcon('ticker')}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                      onClick={() => handleSort('market_cap')}
                    >
                      <div className="flex items-center justify-center">
                        MCap
                        {renderSortIcon('market_cap')}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                      onClick={() => handleSort('enterprise_value')}
                    >
                      <div className="flex items-center justify-center">
                        EV
                        {renderSortIcon('enterprise_value')}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                      onClick={() => handleSort('forward_pe')}
                    >
                      <div className="flex items-center justify-center">
                        P/E
                        {renderSortIcon('forward_pe')}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                      onClick={() => handleSort('ev_to_sales')}
                    >
                      <div className="flex items-center justify-center">
                        EV/S
                        {renderSortIcon('ev_to_sales')}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                      onClick={() => handleSort('price_to_book')}
                    >
                      <div className="flex items-center justify-center">
                        P/B
                        {renderSortIcon('price_to_book')}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                      onClick={() => handleSort('gross_margin')}
                    >
                      <div className="flex items-center justify-center">
                        Gross
                        {renderSortIcon('gross_margin')}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                      onClick={() => handleSort('operating_margin')}
                    >
                      <div className="flex items-center justify-center">
                        Op Mgn
                        {renderSortIcon('operating_margin')}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                      onClick={() => handleSort('dividend_yield')}
                    >
                      <div className="flex items-center justify-center">
                        Div
                        {renderSortIcon('dividend_yield')}
                      </div>
                    </th>
                    <th className="px-2 py-2 text-right hidden sm:table-cell"></th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-600' : 'divide-slate-100'}`}>
                  {getSortedCategoryTickers(category.tickers || []).map((tickerName, idx) => {
                    // Try to find ticker data in displayData first, then fallback to allTickersData
                    let tickerDataItem = displayData.find(d => d.ticker?.toUpperCase() === tickerName?.toUpperCase());
                    if (!tickerDataItem || !tickerDataItem.market_cap) {
                      // Fallback to allTickersData if displayData doesn't have the data
                      tickerDataItem = allTickersData.find(d => d.ticker?.toUpperCase() === tickerName?.toUpperCase());
                    }
                    tickerDataItem = tickerDataItem || {};
                    const rowKey = `cat-${catIdx}-${tickerName}`;
                    const isRowExpanded = expandedRowTicker === rowKey;
                    
                    return (
                      <React.Fragment key={idx}>
                        <tr 
                          className={`cursor-pointer sm:cursor-default ${isDarkMode ? 'bg-slate-900 hover:bg-slate-800' : 'bg-white hover:bg-slate-50'} ${isRowExpanded ? (isDarkMode ? 'bg-slate-800' : 'bg-slate-100') : ''}`}
                          onClick={() => setExpandedRowTicker(prev => prev === rowKey ? null : rowKey)}
                        >
                          {/* Ticker - sticky */}
                          <td className={`px-3 py-2 sticky left-0 ${isDarkMode ? 'bg-slate-900' : 'bg-white'} ${isRowExpanded ? (isDarkMode ? 'bg-slate-800' : 'bg-slate-100') : ''}`}>
                            <span className={`font-mono font-bold text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                              ${tickerName}
                            </span>
                          </td>
                          {/* MCap */}
                          <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {formatNumber(tickerDataItem.market_cap, 'currency')}
                          </td>
                          {/* EV */}
                          <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {formatNumber(tickerDataItem.enterprise_value, 'currency')}
                          </td>
                          {/* P/E */}
                          <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {formatNumber(tickerDataItem.forward_pe, 'ratio')}
                          </td>
                          {/* EV/S */}
                          <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {formatNumber(tickerDataItem.ev_to_sales, 'ratio')}
                          </td>
                          {/* P/B - hidden on mobile */}
                          <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {formatNumber(tickerDataItem.price_to_book, 'ratio')}
                          </td>
                          {/* Gross Margin - hidden on mobile */}
                          <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {tickerDataItem.gross_margin ? `${formatNumber(tickerDataItem.gross_margin * 100, 'ratio')}%` : '-'}
                          </td>
                          {/* Operating Margin - hidden on mobile */}
                          <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {tickerDataItem.operating_margin ? `${formatNumber(tickerDataItem.operating_margin * 100, 'ratio')}%` : '-'}
                          </td>
                          {/* Dividend - hidden on mobile */}
                          <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {tickerDataItem.dividend_yield ? `${formatNumber(tickerDataItem.dividend_yield * 100, 'ratio')}%` : '-'}
                          </td>
                          {/* Desktop action buttons - expanded bubble format */}
                          <td className="px-2 py-2 hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            {/* Dossier button */}
                            <button
                              onClick={() => onDocuments?.({ ticker: tickerName, ...tickerDataItem })}
                              className={`flex flex-col items-center justify-center flex-1 min-w-[45px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-slate-400 hover:bg-slate-500/10' : 'text-slate-600 hover:bg-slate-100'}`}
                              title={`Dossier for $${tickerName}`}
                            >
                              <FileText className="w-5 h-5" />
                              <span className="text-[10px]">File</span>
                            </button>
                            {/* Folder button */}
                            <button
                              onClick={() => onSchedule?.({ ticker: tickerName, ...tickerDataItem })}
                              className={`flex flex-col items-center justify-center flex-1 min-w-[45px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-purple-400 hover:bg-purple-500/10' : 'text-purple-600 hover:bg-purple-50'}`}
                              title={`Add $${tickerName} to folder`}
                            >
                              <FolderIcon className="w-5 h-5" />
                              <span className="text-[10px]">Folder</span>
                            </button>
                            {/* Share button */}
                            <button
                              onClick={() => onShare?.({ ticker: tickerName, ...tickerDataItem })}
                              className={`flex flex-col items-center justify-center flex-1 min-w-[45px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-orange-400 hover:bg-orange-500/10' : 'text-orange-600 hover:bg-orange-50'}`}
                              title={`Share $${tickerName}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                              </svg>
                              <span className="text-[10px]">Share</span>
                            </button>
                            {/* Delete button */}
                            <button
                              onClick={() => handleRemoveTicker(tickerName)}
                              className={`flex flex-col items-center justify-center flex-1 min-w-[45px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`}
                              title={`Remove $${tickerName}`}
                            >
                              <Trash2 className="w-5 h-5" />
                              <span className="text-[10px]">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Mobile Dropdown with action buttons - full width */}
                      <tr className="sm:hidden">
                        <td colSpan={6} className="p-0">
                          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isRowExpanded ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className={`px-2 py-2 border-t ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                              {/* Button Order (left to right): Delete → Share → Folder → File → Original */}
                              <div className="flex items-center justify-between gap-1 px-1">
                                <button onClick={(e) => { e.stopPropagation(); handleRemoveTicker(tickerName); }} className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}>
                                  <Trash2 className="w-6 h-6" />
                                  <span className="text-[10px] sm:text-xs">Delete</span>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); onShare?.({ ticker: tickerName, ...tickerDataItem }); }} className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}>
                                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/></svg>
                                  <span className="text-[10px] sm:text-xs">Share</span>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); onSchedule?.({ ticker: tickerName, ...tickerDataItem }); }} className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}>
                                  <FolderIcon className="w-6 h-6" />
                                  <span className="text-[10px] sm:text-xs">Folder</span>
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); onDocuments?.({ ticker: tickerName, ...tickerDataItem }); }} className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-slate-500/30 text-slate-400 hover:bg-slate-500/10' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                                  <FileText className="w-6 h-6" />
                                  <span className="text-[10px] sm:text-xs">File</span>
                                </button>
                                <button onClick={(e) => { 
                                  e.stopPropagation(); 
                                  onOriginal?.({
                                    ticker: selectedWatchlist.name,
                                    watchlistId: selectedWatchlist.id,
                                    bookmarkId: selectedWatchlist.source_info?.bookmark_id,
                                    sourceUrl: selectedWatchlist.source_info?.source_url,
                                    content: selectedWatchlist.description || selectedWatchlist.source_info?.original_summary,
                                    author: selectedWatchlist.source_info?.author_name || selectedWatchlist.source_info?.author,
                                    sourceType: selectedWatchlist.source_info?.source_type,
                                    isIdeasList: true
                                  }); 
                                }} className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}>
                                  <ExternalLink className="w-6 h-6" />
                                  <span className="text-[10px] sm:text-xs">Open</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
      /* Spreadsheet Table - for non-categorized watchlists */
      <div className={`rounded-lg overflow-hidden border shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="overflow-x-auto relative">
          <table className="w-full text-sm">
            <thead className={`text-xs uppercase tracking-wide ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
              <tr>
                <th 
                  className={`px-3 py-2 text-left sticky left-0 cursor-pointer hover:text-orange-500 ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}
                  onClick={() => handleSort('ticker')}
                >
                  <div className="flex items-center">
                    Ticker
                    {renderSortIcon('ticker')}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                  onClick={() => handleSort('market_cap')}
                >
                  <div className="flex items-center justify-center">
                    MCap
                    {renderSortIcon('market_cap')}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                  onClick={() => handleSort('enterprise_value')}
                >
                  <div className="flex items-center justify-center">
                    EV
                    {renderSortIcon('enterprise_value')}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                  onClick={() => handleSort('forward_pe')}
                >
                  <div className="flex items-center justify-center">
                    P/E
                    {renderSortIcon('forward_pe')}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                  onClick={() => handleSort('ev_to_sales')}
                >
                  <div className="flex items-center justify-center">
                    EV/S
                    {renderSortIcon('ev_to_sales')}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                  onClick={() => handleSort('price_to_book')}
                >
                  <div className="flex items-center justify-center">
                    P/B
                    {renderSortIcon('price_to_book')}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                  onClick={() => handleSort('gross_margin')}
                >
                  <div className="flex items-center justify-center">
                    Gross
                    {renderSortIcon('gross_margin')}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                  onClick={() => handleSort('operating_margin')}
                >
                  <div className="flex items-center justify-center">
                    Op Mgn
                    {renderSortIcon('operating_margin')}
                  </div>
                </th>
                <th 
                  className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                  onClick={() => handleSort('dividend_yield')}
                >
                  <div className="flex items-center justify-center">
                    Div
                    {renderSortIcon('dividend_yield')}
                  </div>
                </th>
                <th className="px-2 py-2 text-right hidden sm:table-cell"></th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-600' : 'divide-slate-100'}`}>
              {loadingMetrics ? (
                <tr>
                  <td colSpan={7} className={`px-3 py-6 sm:py-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mx-auto mb-2" />
                    Loading metrics...
                  </td>
                </tr>
              ) : displayData.length === 0 ? (
                <tr>
                  <td colSpan={7} className={`px-3 py-6 sm:py-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {selectedWatchlist ? 'No tickers in this watchlist' : 'Select a watchlist to view'}
                  </td>
                </tr>
              ) : (
                displayData.map((row, idx) => (
                  <React.Fragment key={row.ticker || idx}>
                    <tr 
                      className={`cursor-pointer sm:cursor-default ${isDarkMode ? 'bg-slate-900 hover:bg-slate-800' : 'bg-white hover:bg-slate-50'} ${expandedRowTicker === row.ticker ? (isDarkMode ? 'bg-slate-800' : 'bg-slate-100') : ''}`}
                      onClick={() => setExpandedRowTicker(prev => prev === row.ticker ? null : row.ticker)}
                    >
                      <td className={`px-3 py-2 sticky left-0 ${isDarkMode ? 'bg-slate-900' : 'bg-white'} ${expandedRowTicker === row.ticker ? (isDarkMode ? 'bg-slate-800' : 'bg-slate-100') : ''}`}>
                        <span className={`font-mono font-bold text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                          ${row.ticker}
                        </span>
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatNumber(row.market_cap, 'currency')}
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatNumber(row.enterprise_value, 'currency')}
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatNumber(row.forward_pe, 'ratio')}
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatNumber(row.ev_to_sales, 'ratio')}
                      </td>
                      {/* P/B - hidden on mobile */}
                      <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatNumber(row.price_to_book, 'ratio')}
                      </td>
                      {/* Gross Margin - hidden on mobile */}
                      <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {row.gross_margin ? `${formatNumber(row.gross_margin * 100, 'ratio')}%` : '-'}
                      </td>
                      {/* Operating Margin - hidden on mobile */}
                      <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {row.operating_margin ? `${formatNumber(row.operating_margin * 100, 'ratio')}%` : '-'}
                      </td>
                      {/* Dividend - hidden on mobile */}
                      <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {row.dividend_yield ? `${formatNumber(row.dividend_yield * 100, 'ratio')}%` : '-'}
                      </td>
                      {/* Desktop action buttons - mobile style format */}
                      <td className="px-2 py-2 hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          <button onClick={() => onDocuments?.(row)} className={`flex flex-col items-center justify-center min-w-[45px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-slate-400 hover:bg-slate-500/10' : 'text-slate-600 hover:bg-slate-100'}`} title={`Dossier for $${row.ticker}`}>
                            <FileText className="w-5 h-5" />
                            <span className="text-[10px]">File</span>
                          </button>
                          <button onClick={() => onSchedule?.(row)} className={`flex flex-col items-center justify-center min-w-[45px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-purple-400 hover:bg-purple-500/10' : 'text-purple-600 hover:bg-purple-50'}`} title={`Add $${row.ticker} to folder`}>
                            <FolderIcon className="w-5 h-5" />
                            <span className="text-[10px]">Folder</span>
                          </button>
                          <button onClick={() => onShare?.(row)} className={`flex flex-col items-center justify-center min-w-[45px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-yellow-400 hover:bg-yellow-500/10' : 'text-yellow-600 hover:bg-yellow-50'}`} title={`Share $${row.ticker}`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/></svg>
                            <span className="text-[10px]">Share</span>
                          </button>
                          <button onClick={() => handleRemoveTicker(row.ticker)} className={`flex flex-col items-center justify-center min-w-[45px] gap-0.5 px-2 py-1.5 rounded-lg text-xs ${isDarkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`} title={`Remove $${row.ticker}`}>
                            <Trash2 className="w-5 h-5" />
                            <span className="text-[10px]">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Mobile Dropdown with action buttons - full width */}
                    <tr className="sm:hidden">
                      <td colSpan={6} className="p-0">
                        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedRowTicker === row.ticker ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'}`}>
                          <div className={`px-2 py-2 border-t ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                            {/* Button Order (left to right): Delete → Share → Folder → File → Original */}
                            <div className="flex items-center justify-between gap-1 px-1">
                              <button onClick={(e) => { e.stopPropagation(); handleRemoveTicker(row.ticker); }} className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}>
                                <Trash2 className="w-6 h-6" />
                                <span className="text-[10px] sm:text-xs">Delete</span>
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); onShare?.(row); }} className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-400 text-yellow-600 hover:bg-yellow-50'}`}>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/></svg>
                                <span className="text-[10px] sm:text-xs">Share</span>
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); onSchedule?.(row); }} className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}>
                                <FolderIcon className="w-6 h-6" />
                                <span className="text-[10px] sm:text-xs">Folder</span>
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); onDocuments?.(row); }} className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-slate-500/30 text-slate-400 hover:bg-slate-500/10' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                                <FileText className="w-6 h-6" />
                                <span className="text-[10px] sm:text-xs">File</span>
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); handleTickerClick(row.ticker); }} className={`flex flex-col sm:flex-row items-center justify-center flex-1 sm:flex-none gap-0.5 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg border-0 sm:border-2 text-xs sm:text-sm ${isDarkMode ? 'border-blue-500/30 text-blue-400 hover:bg-blue-500/10' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                                <span className="text-[10px] sm:text-xs">Open</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )
      )}
          </>
          )}
        </div>
      </CardContent>
      
      {/* Create New Watchlist Modal - matches global search UI */}
      {showCreateWatchlist && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center pt-16 sm:pt-24 p-4" onClick={() => {
          setShowCreateWatchlist(false);
          setSelectedTickers([]);
          setTickerSearchQuery('');
          setTickerSearchResults([]);
          setNewWatchlistName('');
        }}>
          <div 
            className={`${isDarkMode ? 'bg-slate-900' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'} px-4 py-3 flex items-center justify-between border-b`}>
              <h3 className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Create Custom List</h3>
              <button
                onClick={() => {
                  setShowCreateWatchlist(false);
                  setSelectedTickers([]);
                  setTickerSearchQuery('');
                  setNewWatchlistName('');
                }}
                className={`p-1.5 rounded-full ${isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-slate-700'} transition-colors`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Collection Name Input */}
            <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <label className={`block text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} mb-1.5 uppercase tracking-wide`}>
                Collection Name
              </label>
              <input
                type="text"
                placeholder="e.g., Tech Growth, Value Picks..."
                value={newWatchlistName}
                onChange={(e) => setNewWatchlistName(e.target.value)}
                className={`w-full ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'} border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
                autoFocus
              />
            </div>
            
            {/* Search Input */}
            <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <label className={`block text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} mb-1.5 uppercase tracking-wide`}>
                Add Tickers
              </label>
              
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                <input
                  type="text"
                  placeholder="Search by $ticker, company name, or @author..."
                  value={tickerSearchQuery}
                  onChange={(e) => setTickerSearchQuery(e.target.value)}
                  className={`w-full ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'} border rounded-lg pl-11 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent`}
                />
                {isSearchingTickers && (
                  <RefreshCw className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-orange-500 animate-spin" />
                )}
              </div>
            </div>
            
            {/* Search Results or Selected Tickers */}
            <div className="max-h-80 overflow-y-auto">
              {tickerSearchQuery && tickerSearchResults.length > 0 ? (
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-green-500 text-sm font-medium">
                      Found {tickerSearchResults.length} result{tickerSearchResults.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    {tickerSearchResults.slice(0, 8).map((idea, idx) => {
                      const isTickerAlreadyAdded = selectedTickers.find(t => t.ticker === idea.ticker);
                      return (
                        <button
                          key={idea.idea_id || idx}
                          onClick={() => !isTickerAlreadyAdded && addTickerToSelection(idea)}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            isTickerAlreadyAdded 
                              ? 'bg-orange-500/20 border border-orange-500/50 cursor-default' 
                              : isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isTickerAlreadyAdded ? 'bg-orange-500' : isDarkMode ? 'bg-slate-700' : 'bg-slate-300'
                            }`}>
                              {isTickerAlreadyAdded ? (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <span className={`text-xs font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{idx + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-orange-500 font-bold text-sm">
                                  ${idea.ticker}
                                </span>
                                {isTickerAlreadyAdded && (
                                  <span className="px-1.5 py-0.5 bg-orange-500/30 text-orange-600 text-xs rounded">
                                    Added
                                  </span>
                                )}
                                {idea.is_ticker_list && (
                                  <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-500 text-xs rounded">
                                    Ideas List
                                  </span>
                                )}
                              </div>
                              <p className={`text-sm truncate ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                {idea.company_name || idea.ticker}
                              </p>
                              {idea.author && (
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  @{idea.author}
                                </p>
                              )}
                            </div>
                            {!isTickerAlreadyAdded ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addTickerToSelection(idea);
                                  setTickerSearchQuery('');
                                }}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                  isDarkMode 
                                    ? 'bg-orange-600 hover:bg-orange-500 text-white' 
                                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                                }`}
                              >
                                Add
                              </button>
                            ) : (
                              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : tickerSearchQuery && !isSearchingTickers && tickerSearchResults.length === 0 ? (
                <div className="p-4 text-center">
                  <svg className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>No ideas found for "{tickerSearchQuery}"</p>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>Try searching for a ticker or author from your saved ideas</p>
                </div>
              ) : tickerSearchQuery && isSearchingTickers ? (
                <div className="p-4 text-center">
                  <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                  <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Searching your ideas...</p>
                </div>
              ) : selectedTickers.length > 0 ? (
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      Selected Tickers ({selectedTickers.length})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {selectedTickers.map((ticker, idx) => (
                      <div
                        key={ticker.ticker}
                        className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-white">{idx + 1}</span>
                          </div>
                          <div>
                            <span className="text-orange-500 font-bold text-sm">${ticker.ticker}</span>
                            <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{ticker.company_name}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeTickerFromSelection(ticker.ticker)}
                          className={`p-1.5 rounded-full hover:bg-red-500/20 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} hover:text-red-500 transition-colors`}
                        >
                          <X className="w-6 h-6" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-4">
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Search your saved ideas to add tickers to this list
                  </p>
                  <div className="space-y-2">
                    <p className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`}>Try searching for:</p>
                    {['@', 'tech', 'growth'].map((suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => setTickerSearchQuery(suggestion)}
                        className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${isDarkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                      >
                        {suggestion === '@' ? '@author name' : suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className={`px-4 py-3 border-t flex items-center justify-end ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowCreateWatchlist(false);
                    setNewWatchlistName('');
                    setSelectedTickers([]);
                    setTickerSearchQuery('');
                  }}
                  className={`px-4 py-2 font-medium transition-colors ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateWatchlist}
                  disabled={!newWatchlistName.trim() || creatingWatchlist}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {creatingWatchlist ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-6 h-6" />
                      Create Watchlist
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Add to Existing Watchlist Modal */}
      {showAddToWatchlist && selectedWatchlist && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center pt-16 sm:pt-24 p-4" onClick={() => {
          setShowAddToWatchlist(false);
          setSelectedTickers([]);
          setTickerSearchQuery('');
          setTickerSearchResults([]);
        }}>
          <div 
            className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-slate-800 px-4 py-3 flex items-center justify-between border-b border-slate-700">
              <h3 className="text-sm font-bold text-white">Add to "{selectedWatchlist.name}"</h3>
              <button
                onClick={() => {
                  setShowAddToWatchlist(false);
                  setSelectedTickers([]);
                  setTickerSearchQuery('');
                }}
                className="p-1.5 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Search Input */}
            <div className="px-4 py-3 border-b border-slate-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by $ticker, @handle, company name..."
                  value={tickerSearchQuery}
                  onChange={(e) => setTickerSearchQuery(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-11 pr-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  autoFocus
                />
                {isSearchingTickers && (
                  <RefreshCw className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-orange-500 animate-spin" />
                )}
              </div>
            </div>
            
            {/* Search Results */}
            <div className="max-h-80 overflow-y-auto">
              {tickerSearchQuery && tickerSearchResults.length > 0 ? (
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-green-500 text-sm font-medium">
                      Found {tickerSearchResults.length} result{tickerSearchResults.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    {tickerSearchResults.slice(0, 8).map((idea, idx) => {
                      const isTickerAlreadyAdded = selectedTickers.find(t => t.ticker === idea.ticker);
                      const isAlreadyInWatchlist = selectedWatchlist.tickers?.includes(idea.ticker);
                      return (
                        <button
                          key={idea.idea_id || idx}
                          onClick={() => !isTickerAlreadyAdded && !isAlreadyInWatchlist && addTickerToSelection(idea)}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            isAlreadyInWatchlist
                              ? 'bg-slate-700/50 cursor-default opacity-60'
                              : isTickerAlreadyAdded 
                                ? 'bg-orange-500/20 border border-orange-500/50 cursor-default' 
                                : 'bg-slate-800 hover:bg-slate-700'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isAlreadyInWatchlist ? 'bg-slate-600' :
                              isTickerAlreadyAdded ? 'bg-orange-500' : 'bg-slate-700'
                            }`}>
                              {isAlreadyInWatchlist ? (
                                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              ) : isTickerAlreadyAdded ? (
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <span className="text-xs font-bold text-slate-300">{idx + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-orange-400 font-bold text-sm">
                                  ${idea.ticker}
                                </span>
                                {isAlreadyInWatchlist && (
                                  <span className="px-1.5 py-0.5 bg-slate-600 text-slate-300 text-xs rounded">
                                    In watchlist
                                  </span>
                                )}
                                {isTickerAlreadyAdded && !isAlreadyInWatchlist && (
                                  <span className="px-1.5 py-0.5 bg-orange-500/30 text-orange-300 text-xs rounded">
                                    Selected
                                  </span>
                                )}
                                {idea.is_ticker_list && (
                                  <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">
                                    Ideas List
                                  </span>
                                )}
                              </div>
                              <p className="text-white text-sm truncate">
                                {idea.company_name || idea.ticker}
                              </p>
                              {idea.author && (
                                <p className="text-slate-400 text-xs mt-1">
                                  @{idea.author}
                                </p>
                              )}
                            </div>
                            {!isTickerAlreadyAdded && !isAlreadyInWatchlist && (
                              <Plus className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : tickerSearchQuery && !isSearchingTickers && tickerSearchResults.length === 0 ? (
                <div className="p-4 text-center">
                  <svg className="w-12 h-12 text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-slate-400">No results found for "{tickerSearchQuery}"</p>
                </div>
              ) : tickerSearchQuery && isSearchingTickers ? (
                <div className="p-4 text-center">
                  <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                  <p className="text-slate-400">Searching...</p>
                </div>
              ) : selectedTickers.length > 0 ? (
                <div className="p-4">
                  <div className="text-slate-400 text-sm font-medium mb-3">
                    Selected ({selectedTickers.length})
                  </div>
                  <div className="space-y-2">
                    {selectedTickers.map((ticker) => (
                      <div
                        key={ticker.ticker}
                        className="flex items-center justify-between p-3 bg-slate-800 rounded-lg"
                      >
                        <span className="text-orange-400 font-bold">${ticker.ticker}</span>
                        <button
                          onClick={() => removeTickerFromSelection(ticker.ticker)}
                          className="p-1.5 rounded-full hover:bg-red-500/20 text-slate-400 hover:text-red-400"
                        >
                          <X className="w-6 h-6" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-4 text-center text-slate-500">
                  Search for tickers to add to this list
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="px-4 py-3 bg-slate-800 border-t border-slate-700 flex items-center justify-between">
              <div className="text-sm text-slate-400">
                {selectedTickers.length > 0 && `${selectedTickers.length} ticker${selectedTickers.length !== 1 ? 's' : ''} selected`}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowAddToWatchlist(false);
                    setSelectedTickers([]);
                    setTickerSearchQuery('');
                  }}
                  className="px-4 py-2 text-slate-400 hover:text-white transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddToWatchlist}
                  disabled={selectedTickers.length === 0 || addingToWatchlist}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {addingToWatchlist ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-6 h-6" />
                      Add to List
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
    </div>
  );
}

export default WatchlistSpreadsheet;
